import React, { useCallback, useEffect, useRef, useState } from "react";
import L, { type LatLng, type LatLngBounds, type LeafletMouseEvent, type TileLayer } from "leaflet";
import "leaflet/dist/leaflet.css";
import "./Mapper.css";
import Menu, { type MenuItem } from "../components/Menu";

type Mode = "select" | "addIsland";
type StatusTone = "ready" | "warn" | "error";

type AccessType = "entrance" | "exit" | "both";

type AccessEntry = {
  layer: L.Marker;
  type: AccessType;
  rotation: number;
  setType: (type: AccessType) => void;
  remove: () => void;
};

type HoveredHandle = { lot: Lot; index: number };
type HoveredEdge = { lot: Lot; latlng: LatLng };

type IslandEntry = { layer: L.Rectangle };
type CameraEntry = {
  id: string;
  marker: L.Marker;
  dir: 1 | -1;
};

type Lot = {
  id: string;
  polygon: L.Polygon;
  labelMarker: L.Marker;
  labelElement: HTMLDivElement;
  entrances: AccessEntry[];
  exits: AccessEntry[];
  islands: IslandEntry[];
  handles: L.Marker[];
};

type Suggestion = { name: string; lat: number; lon: number };

const DEFAULT_VIEW: [number, number] = [45.4215, -75.6972];
const VIEW_STORAGE_KEY = "mapper:last-view";
const NOMINATIM_BASE = "https://nominatim.openstreetmap.org";
const PHOTON_BASE = "https://photon.komoot.io";

type StoredView = { lat: number; lng: number; zoom: number; base: "esri" | "carto" };

const fetchJsonWithTimeout = async (url: string, timeoutMs = 6500) => {
  if (typeof window === "undefined") return null;
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { headers: { "Accept-Language": "en" }, signal: controller.signal });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
  }
};

const loadStoredView = (): StoredView | null => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw = window.localStorage.getItem(VIEW_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== "object" ||
      typeof parsed.lat !== "number" ||
      typeof parsed.lng !== "number" ||
      typeof parsed.zoom !== "number" ||
      (parsed.base !== "esri" && parsed.base !== "carto")
    ) {
      return null;
    }
    return { lat: parsed.lat, lng: parsed.lng, zoom: parsed.zoom, base: parsed.base };
  } catch (err) {
    console.warn("Failed to load stored view", err);
    return null;
  }
};

const persistStoredView = (center: LatLng, zoom: number, base: "esri" | "carto") => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    const payload: StoredView = { lat: center.lat, lng: center.lng, zoom, base };
    window.localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify(payload));
  } catch (err) {
    console.warn("Failed to persist view", err);
  }
};

const makeLotStyle = (selected: boolean): L.PolylineOptions => ({
  color: selected ? "#1782ff" : "#0ea5e9",
  weight: selected ? 3 : 2,
  fillColor: "#0ea5e9",
  fillOpacity: 0.35,
});

const Mapper: React.FC = () => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const baseLayersRef = useRef<{ esri: TileLayer | null; carto: TileLayer | null }>({
    esri: null,
    carto: null,
  });
  const initialStoredViewRef = useRef<StoredView | null>(loadStoredView());
  const activeBaseRef = useRef<"esri" | "carto">(initialStoredViewRef.current?.base ?? "esri");
  const switchBaseRef = useRef<(next: "esri" | "carto") => void>(() => {});
  const lotsRef = useRef<Lot[]>([]);
  const camerasRef = useRef<CameraEntry[]>([]);
  const selectedLotIdRef = useRef<string | null>(null);
  const selectedLotIdsRef = useRef<Set<string>>(new Set());
  const lotCounterRef = useRef<number>(1);
  const draggingIslandRef = useRef<L.Rectangle | null>(null);
  const dragIslandDimsRef = useRef<{ latSpan: number; lngSpan: number } | null>(null);
  const dragIslandOffsetRef = useRef<{ lat: number; lng: number } | null>(null);
  const vertexHandleIconRef = useRef<L.DivIcon>(
    L.divIcon({
      className: "mapper-vertex-handle",
      iconSize: [24, 24],
      iconAnchor: [12, 12],
    })
  );

  const [mode, setMode] = useState<Mode>("select");
  const [placementType, setPlacementType] = useState<AccessType>("entrance");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [status, setStatus] = useState<{ text: string; tone: StatusTone }>(
    () => ({ text: "Zoom in closer before auto-detecting lots", tone: "warn" })
  );
  const [isDetecting, setIsDetecting] = useState<boolean>(false);
  const [selectedLotId, setSelectedLotId] = useState<string | null>(null);
  const [, setSelectedLotIds] = useState<string[]>([]);
  const [activeBase, setActiveBase] = useState<"esri" | "carto">(
    initialStoredViewRef.current?.base ?? "esri"
  );
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [mapAddress, setMapAddress] = useState<string>("Fetching location…");
  const [mapCoords, setMapCoords] = useState<string>("");
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
  const [lotCount, setLotCount] = useState<number>(0);
  const [zoomReady, setZoomReady] = useState<boolean>(true);
  const [addLotMode, setAddLotMode] = useState<boolean>(false);
  const [statusVisible, setStatusVisible] = useState<boolean>(true);
  const [searchFocused, setSearchFocused] = useState<boolean>(false);
  const [shadeEnabled, setShadeEnabled] = useState<boolean>(false);
  const [shadeHour, setShadeHour] = useState<number>(14);
  const [shadeDate, setShadeDate] = useState<Date>(() => new Date());
  const [shadeMinHour, setShadeMinHour] = useState<number>(6);
  const [shadeMaxHour, setShadeMaxHour] = useState<number>(18);
  const shadeJustEnabledRef = useRef<boolean>(false);
  const shadeRafRef = useRef<number | null>(null);
  const addLotModeRef = useRef<boolean>(false);
  const [addLotPointsVersion, setAddLotPointsVersion] = useState<number>(0);
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const shadeDateInputRef = useRef<HTMLInputElement | null>(null);
  const statusTimerRef = useRef<number | null>(null);
  const statusClearRef = useRef<number | null>(null);
  const statusRef = useRef(status);
  const searchBlurTimerRef = useRef<number | null>(null);
  const shadowLayerRef = useRef<L.LayerGroup | null>(null);
  const shadowBuildingsRef = useRef<any[]>([]);
  const [shadowLoading, setShadowLoading] = useState<boolean>(false);
  const shadowLastStateRef = useRef<{ center: LatLng | null; zoom: number | null }>({
    center: null,
    zoom: null,
  });
  const shadowCooldownRef = useRef<number | null>(null);
  const MIN_SHADOW_ZOOM = 16.5;
  const [, setAccessVersion] = useState<number>(0);
  const [paletteDrag, setPaletteDrag] = useState<{ type: AccessType; x: number; y: number } | null>(
    null
  );
  const [showDeleteHint, setShowDeleteHint] = useState<boolean>(false);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    items: MenuItem[];
  } | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState<boolean>(false);
  const [infoExpanded, setInfoExpanded] = useState<boolean>(false);
  const closeMobileSidebarAfterAction = useCallback(() => {
    if (isMobile) {
      // Defer closing so the originating click/tap can finish firing (avoids mobile canceling actions)
      window.setTimeout(() => setMobileSidebarOpen(false), 10);
    }
  }, [isMobile]);
  const runActionThenClose = useCallback(
    async (action: () => void | Promise<void>) => {
      const result = action();
      if (result && typeof (result as Promise<void>).then === "function") {
        try {
          await result;
        } catch {
          // ignore to allow sidebar close even if action rejects
        }
      }
      closeMobileSidebarAfterAction();
    },
    [closeMobileSidebarAfterAction]
  );

  const palettePendingDragRef = useRef<{ type: AccessType; startX: number; startY: number } | null>(null);
  const palettePreDragMoveRef = useRef<((e: PointerEvent) => void) | null>(null);
  const palettePreDragUpRef = useRef<((e: PointerEvent) => void) | null>(null);
  const paletteDragTypeRef = useRef<AccessType | null>(null);
  const paletteMoveHandlerRef = useRef<((e: PointerEvent) => void) | null>(null);
  const paletteUpHandlerRef = useRef<((e: PointerEvent) => void) | null>(null);
  const suppressNextClickRef = useRef<boolean>(false);
  const rotatingAccessRef = useRef<AccessEntry | null>(null);
  const rotateMoveHandlerRef = useRef<((e: PointerEvent) => void) | null>(null);
  const rotateDownHandlerRef = useRef<((e: PointerEvent) => void) | null>(null);
  const hoveredAccessRef = useRef<AccessEntry | null>(null);
  const hoveredHandleRef = useRef<HoveredHandle | null>(null);
  const hoveredEdgeRef = useRef<HoveredEdge | null>(null);
  const hoveredLotRef = useRef<Lot | null>(null);
  useEffect(() => {
    const sync = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    sync();
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, []);
  useEffect(() => {
    if (!isMobile) {
      setMobileSidebarOpen(false);
    }
  }, [isMobile, setMobileSidebarOpen]);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);
  const addLotPointsRef = useRef<LatLng[]>([]);
  const addLotMarkersRef = useRef<L.Marker[]>([]);
  const addLotSegmentsRef = useRef<L.Polyline[]>([]);
  const addLotClosingRef = useRef<L.Polyline | null>(null);
  useEffect(() => {
    addLotModeRef.current = addLotMode;
  }, [addLotMode]);

  const modeRef = useRef<Mode>("select");
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  const addressTimerRef = useRef<number | null>(null);
  const persistCurrentView = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const center = map.getCenter();
    const zoom = map.getZoom();
    persistStoredView(center, zoom, activeBaseRef.current);
  }, []);

  const setStatusMessage = useCallback(
    (text: string, tone: StatusTone = "ready", durationMs?: number | null) => {
      if (statusTimerRef.current) {
        window.clearTimeout(statusTimerRef.current);
        statusTimerRef.current = null;
      }
      if (statusClearRef.current) {
        window.clearTimeout(statusClearRef.current);
        statusClearRef.current = null;
      }
      const hasText = Boolean(text);
      setStatus({ text, tone });
      statusRef.current = { text, tone };
      setStatusVisible(hasText);
      if (!hasText) return;
      const auto =
        durationMs !== undefined
          ? durationMs
          : tone === "ready"
          ? 1600
          : tone === "warn"
          ? 4000
          : null;
      if (auto) {
        statusTimerRef.current = window.setTimeout(() => {
          setStatusVisible(false);
          statusClearRef.current = window.setTimeout(() => {
            setStatus((prev) => ({ ...prev, text: "" }));
            statusClearRef.current = null;
          }, 240);
          statusTimerRef.current = null;
        }, auto);
      }
    },
    []
  );

  const getMap = useCallback(() => mapRef.current, []);
  const getSidebarRect = () => sidebarRef.current?.getBoundingClientRect() || null;
  const getMapRect = () => mapContainerRef.current?.getBoundingClientRect() || null;
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null);
  const bumpAccessVersion = useCallback(() => setAccessVersion((v) => v + 1), []);
  const withKeyHint = (label: string, key: string) => (
    <span className="menu-label-with-key">
      <span className="menu-label-text">{label}</span>
      <span className="menu-key-hint">
        <img src="/assets/mapper/command.svg" alt="" />
        <span>{key}</span>
      </span>
    </span>
  );
  const setAccessRotatingState = useCallback((entry: AccessEntry, rotating: boolean) => {
    const el = entry.layer.getElement();
    const shell = el?.querySelector<HTMLDivElement>(".access-marker-shell");
    if (!shell) return;
    shell.classList.toggle("rotating", rotating);
  }, []);
  const menuIcon = (src: string, alt: string) => <img src={src} alt={alt} />;
  const clearAddLotDraft = useCallback(() => {
    const map = getMap();
    addLotMarkersRef.current.forEach((m) => map?.removeLayer(m));
    addLotSegmentsRef.current.forEach((s) => map?.removeLayer(s));
    if (addLotClosingRef.current && map) {
      map.removeLayer(addLotClosingRef.current);
    }
    addLotMarkersRef.current = [];
    addLotSegmentsRef.current = [];
    addLotPointsRef.current = [];
    addLotClosingRef.current = null;
    setAddLotPointsVersion((v) => v + 1);
  }, [getMap]);
  const setAccessRotation = useCallback((entry: AccessEntry, rotation: number) => {
    entry.rotation = rotation;
    const el = entry.layer.getElement();
    if (!el) return;
    const img = el.querySelector<HTMLImageElement>(".access-marker-img");
    if (img) {
      img.style.transform = `rotate(${rotation}deg)`;
    }
  }, []);

  const stopAccessRotation = useCallback(
    (message?: { text: string; tone?: StatusTone }) => {
      const active = rotatingAccessRef.current;
      if (active) {
        setAccessRotatingState(active, false);
      }
      if (rotateMoveHandlerRef.current) {
        window.removeEventListener("pointermove", rotateMoveHandlerRef.current);
        rotateMoveHandlerRef.current = null;
      }
      if (rotateDownHandlerRef.current) {
        window.removeEventListener("pointerdown", rotateDownHandlerRef.current, true);
        rotateDownHandlerRef.current = null;
      }
      rotatingAccessRef.current = null;
      if (message) {
        setStatusMessage(message.text, message.tone ?? "ready");
      }
    },
    [setAccessRotatingState, setStatusMessage]
  );

  const startAccessRotation = useCallback(
    (entry: AccessEntry) => {
      stopAccessRotation();
      rotatingAccessRef.current = entry;
      setAccessRotatingState(entry, true);
      const aimAt = (clientX: number, clientY: number, target: AccessEntry) => {
        const markerEl = target.layer.getElement();
        if (!markerEl) return;
        const rect = markerEl.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        let angle = Math.atan2(clientY - cy, clientX - cx) * (180 / Math.PI) + 90;
        const current = target.rotation;
        if (Number.isFinite(current)) {
          while (angle - current > 180) angle -= 360;
          while (angle - current < -180) angle += 360;
        }
        setAccessRotation(target, angle);
      };
      const move = (evt: PointerEvent) => {
        const active = rotatingAccessRef.current;
        if (!active) return;
        aimAt(evt.clientX, evt.clientY, active);
      };
      const down = (evt: PointerEvent) => {
        if (evt.button !== 0 && evt.button !== 2) return;
        const active = rotatingAccessRef.current;
        if (!active) return;
        evt.preventDefault();
        evt.stopPropagation();
        stopAccessRotation({ text: "Rotation set", tone: "ready" });
        bumpAccessVersion();
      };
      rotateMoveHandlerRef.current = move;
      rotateDownHandlerRef.current = down;
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerdown", down, true);
      const last = lastPointerRef.current;
      if (last) {
        aimAt(last.x, last.y, entry);
      }
      setStatusMessage("Move cursor to aim, click to set rotation", "warn");
    },
    [bumpAccessVersion, setAccessRotatingState, setAccessRotation, setStatusMessage, stopAccessRotation]
  );

  const updateAddLotClosingLine = useCallback(() => {
    const map = getMap();
    if (!map) return;
    if (addLotClosingRef.current) {
      map.removeLayer(addLotClosingRef.current);
      addLotClosingRef.current = null;
    }
    const pts = addLotPointsRef.current;
    if (pts.length < 3) return;
    const first = pts[0];
    const last = pts[pts.length - 1];
    const line = L.polyline([last, first], {
      color: "#1782ff",
      weight: 2,
      dashArray: "6 6",
      opacity: 0.5,
    }).addTo(map);
    addLotClosingRef.current = line;
  }, [getMap]);

  const sanitizeFilename = (name: string) => {
    const cleaned = name.replace(/[^a-z0-9-_]+/gi, "_").replace(/_+/g, "_").replace(/^_+|_+$/g, "");
    return cleaned || "mapper-export";
  };

  const addDraftLotPoint = useCallback(
    (latlng: LatLng) => {
      const map = getMap();
      if (!map) return;
      const marker = L.marker(latlng, {
        icon: vertexHandleIconRef.current,
        zIndexOffset: 800,
      }).addTo(map);
      const pts = addLotPointsRef.current;
      if (pts.length) {
        const prev = pts[pts.length - 1];
        const seg = L.polyline([prev, latlng], { color: "#1782ff", weight: 2 }).addTo(map);
      addLotSegmentsRef.current.push(seg);
    }
    addLotMarkersRef.current.push(marker);
    pts.push(latlng);
    updateAddLotClosingLine();
    setAddLotPointsVersion((v) => v + 1);
  },
  [getMap, updateAddLotClosingLine]
);

  const undoDraftLotPoint = useCallback(() => {
    const map = getMap();
    if (!map) return;
    const pts = addLotPointsRef.current;
    if (!pts.length) return;
    const marker = addLotMarkersRef.current.pop();
    if (marker && map.hasLayer(marker)) {
      map.removeLayer(marker);
    }
    const seg = addLotSegmentsRef.current.pop();
    if (seg && map.hasLayer(seg)) {
      map.removeLayer(seg);
    }
    pts.pop();
    updateAddLotClosingLine();
    setAddLotPointsVersion((v) => v + 1);
  }, [getMap, updateAddLotClosingLine]);

  const cancelAddLotDraft = useCallback(() => {
    addLotModeRef.current = false;
    setAddLotMode(false);
    clearAddLotDraft();
    setStatusMessage("Add lot canceled", "warn");
    setAddLotPointsVersion((v) => v + 1);
  }, [clearAddLotDraft, setStatusMessage]);


  const isOverSidebar = (point: { x: number; y: number } | null) => {
    const rect = getSidebarRect();
    if (!rect || !point) return false;
    return point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom;
  };

  const clearLotHandles = useCallback((lot: Lot) => {
    const map = getMap();
    if (!map || !lot.handles.length) return;
    lot.handles.forEach((h) => {
      if (map.hasLayer(h)) map.removeLayer(h);
    });
    lot.handles = [];
  }, []);

  const clearLotExtras = useCallback(
    (lot: Lot) => {
      const map = getMap();
      if (!map) return;
      lot.entrances.forEach((m) => map.removeLayer(m.layer));
      lot.exits.forEach((m) => map.removeLayer(m.layer));
      lot.islands.forEach((r) => map.removeLayer(r.layer));
      clearLotHandles(lot);
      lot.entrances = [];
      lot.exits = [];
      lot.islands = [];
    },
    [clearLotHandles]
  );

  const updateLotLabelPosition = useCallback((lot: Lot) => {
    const center = lot.polygon.getBounds().getCenter();
    lot.labelMarker.setLatLng(center);
  }, []);

  const openContextMenu = (items: MenuItem[], clientX: number, clientY: number) => {
    setContextMenu({ items, x: clientX, y: clientY });
  };

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  const insertPointIntoLot = (lot: Lot, latlng: LatLng) => {
    const map = getMap();
    if (!map) return;
    const pts = getPolygonPoints(lot.polygon);
    if (pts.length < 2) return;
    const p = map.latLngToLayerPoint(latlng);
    let bestIdx = 0;
    let bestDist = Number.POSITIVE_INFINITY;
    const count = pts.length;
    for (let i = 0; i < count; i++) {
      const j = (i + 1) % count;
      const a = map.latLngToLayerPoint(pts[i]);
      const b = map.latLngToLayerPoint(pts[j]);
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const denom = dx * dx + dy * dy || 1;
      let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / denom;
      t = Math.min(1, Math.max(0, t));
      const proj = L.point(a.x + dx * t, a.y + dy * t);
      const distSq = (proj.x - p.x) * (proj.x - p.x) + (proj.y - p.y) * (proj.y - p.y);
      if (distSq < bestDist) {
        bestDist = distSq;
        bestIdx = i;
      }
    }
    const next = [...pts];
    next.splice(bestIdx + 1, 0, latlng);
    lot.polygon.setLatLngs(next);
    lot.polygon.redraw();
    updateLotLabelPosition(lot);
    refreshLotHandles(lot);
  };

  const refreshLotHandles = useCallback(
    (lot: Lot) => {
      const map = getMap();
      if (!map) return;
      clearLotHandles(lot);
      const pts = getPolygonPoints(lot.polygon);
      lot.handles = pts.map((pt, idx) => {
        const marker = L.marker(pt, {
          icon: vertexHandleIconRef.current,
          draggable: true,
          zIndexOffset: 900,
        }).addTo(map);
        marker.on("dragstart", () => map.dragging.disable());
        marker.on("drag", () => {
          const current = getPolygonPoints(lot.polygon);
          const next = current.map((p, i) => (i === idx ? marker.getLatLng() : p));
          lot.polygon.setLatLngs(next);
          lot.polygon.redraw();
          updateLotLabelPosition(lot);
        });
        marker.on("dragend", () => {
          map.dragging.enable();
        });
        marker.on("mouseover", () => {
          hoveredHandleRef.current = { lot, index: idx };
        });
        marker.on("mouseout", () => {
          if (hoveredHandleRef.current?.lot === lot && hoveredHandleRef.current.index === idx) {
            hoveredHandleRef.current = null;
          }
        });
        marker.on("contextmenu", (evt: L.LeafletMouseEvent) => {
          evt.originalEvent.preventDefault();
          evt.originalEvent.stopPropagation();
          focusLotSelection(lot.id);
          const clientX = (evt.originalEvent as MouseEvent).clientX;
          const clientY = (evt.originalEvent as MouseEvent).clientY;
          openContextMenu(
            [
              {
                key: "delete-point",
                label: withKeyHint("Delete Point", "Del"),
                icon: menuIcon("/assets/mapper/remove-handle.svg", "Delete point"),
                onSelect: () => {
                  const pts = getPolygonPoints(lot.polygon);
                  if (pts.length <= 3) {
                    setStatusMessage("A lot needs at least 3 points", "warn");
                    closeContextMenu();
                    return;
                  }
                  const next = pts.filter((_, i) => i !== idx);
                  lot.polygon.setLatLngs(next);
                  lot.polygon.redraw();
                  updateLotLabelPosition(lot);
                  refreshLotHandles(lot);
                  closeContextMenu();
                },
              },
            ],
            clientX,
            clientY
          );
        });
        return marker;
      });
    },
    [clearLotHandles, getMap, updateLotLabelPosition]
  );

  const applySelection = useCallback(
    (nextSelected: Set<string>, primaryId: string | null) => {
      const map = getMap();
      selectedLotIdsRef.current = nextSelected;
      const resolvedPrimary =
        primaryId && nextSelected.has(primaryId)
          ? primaryId
          : nextSelected.size
          ? firstOfSet(nextSelected)
          : null;
      selectedLotIdRef.current = resolvedPrimary;
      setSelectedLotIds(Array.from(nextSelected));
      setSelectedLotId(resolvedPrimary);
      lotsRef.current.forEach((lot) => {
        const isSelected = nextSelected.has(lot.id);
        lot.polygon.setStyle(makeLotStyle(isSelected));
        lot.labelElement.classList.toggle("lot-selected", isSelected);
        if (isSelected && resolvedPrimary === lot.id) {
          refreshLotHandles(lot);
        } else {
          clearLotHandles(lot);
        }
      });
      if (map && !resolvedPrimary) {
        map.dragging.enable();
      }
    },
    [clearLotHandles, getMap, refreshLotHandles]
  );

  const applyLotGeometry = useCallback(
    (lot: Lot, latlngs: LatLng[], preserveExtras = false) => {
      const map = getMap();
      if (!map) return;
      if (!preserveExtras) {
        clearLotExtras(lot);
      } else {
        clearLotHandles(lot);
      }
      lot.polygon.setLatLngs(latlngs);
      lot.polygon.redraw();
      updateLotLabelPosition(lot);
      refreshLotHandles(lot);
      bumpAccessVersion();
    },
    [bumpAccessVersion, clearLotExtras, clearLotHandles, getMap, refreshLotHandles, updateLotLabelPosition]
  );

  const resetLotGeometry = useCallback((lot: Lot, latlngs: LatLng[]) => {
    applyLotGeometry(lot, latlngs, false);
  }, [applyLotGeometry]);

  const detectLotsWithinBounds = async (b: LatLngBounds): Promise<any[]> => {
    const map = getMap();
    if (!map) return [];
    const s = b.getSouth();
    const w = b.getWest();
    const n = b.getNorth();
    const e = b.getEast();

    const area = Math.abs((n - s) * (e - w));
    // Overpass rejects massive bounding boxes; prompt the user to zoom in instead of returning junk
    if (area > 0.05) {
      setStatusMessage("Zoom in further to auto-detect mapped lots", "warn");
      return [];
    }

    const overpassQuery = `
[out:json][timeout:25];
(
  way["amenity"="parking"](${s},${w},${n},${e});
  relation["amenity"="parking"](${s},${w},${n},${e});
);
out geom;`;

    const endpoints = [
      "https://overpass-api.de/api/interpreter",
      "https://overpass.kumi.systems/api/interpreter",
      "https://z.overpass-api.de/api/interpreter",
    ];

    for (const endpoint of endpoints) {
      try {
        const controller = new AbortController();
        const timer = window.setTimeout(() => controller.abort(), 12000);
        const res = await fetch(`${endpoint}?data=${encodeURIComponent(overpassQuery)}`, {
          signal: controller.signal,
          headers: { "Accept-Language": "en" },
        });
        window.clearTimeout(timer);
        if (!res.ok) continue;
        const data = await res.json();
        if (data && data.elements && data.elements.length) {
          return data.elements;
        }
      } catch (err) {
        // try the next endpoint on failure/timeout
        continue;
      }
    }

    return [];
  };

  const formatAddress = (data: any) => {
    if (data?.address) {
      const a = data.address;
      const parts = [a.road || a.neighbourhood || a.suburb, a.city || a.town || a.village, a.state, a.country];
      return parts.filter(Boolean).join(", ");
    }
    if (typeof data?.display_name === "string") {
      const parts = data.display_name.split(",").map((p: string) => p.trim());
      const keep = parts
        .filter((_: string, i: number) => i === 0 || i === parts.length - 1 || i === parts.length - 2)
        .slice(0, 4);
      return keep.join(", ");
    }
    return "Unknown location";
  };

  const formatSuggestionName = (displayName: string) => {
    const parts = displayName.split(",").map((p) => p.trim());
    if (!parts.length) return displayName;
    const primary = parts[0];
    const tail = parts.slice(-3).filter(Boolean).join(", ");
    return tail ? `${primary}, ${tail}` : primary;
  };

  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const toDeg = (rad: number) => (rad * 180) / Math.PI;

  const formatShadeLabel = (hour: number) => {
    // Reflect the exact time we feed into sunPosition
    const base = new Date(shadeDate);
    base.setHours(0, 0, 0, 0);
    const labelDate = new Date(base.getTime() + hour * 60 * 60 * 1000);
    return labelDate.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  };

  const formatDateInput = (date: Date) => {
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, "0");
    const d = `${date.getDate()}`.padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const parseDateInput = (value: string) => {
    const [y, m, d] = value.split("-").map((n) => parseInt(n, 10));
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  };

  const normalizeSunWindow = (sunrise: number, sunset: number) => {
    const norm = (h: number) => ((h % 24) + 24) % 24;
    const sr = norm(sunrise);
    let span = sunset - sunrise;
    if (!Number.isFinite(span) || span <= 0) {
      const ssNorm = norm(sunset);
      span = ssNorm >= sr ? ssNorm - sr : ssNorm + 24 - sr;
    }
    span = Math.min(Math.max(span, 0.5), 23.5); // avoid zero-span and absurd lengths
    const ss = sr + span;
    return { sunrise: sr, sunset: ss };
  };

  const sunPosition = (date: Date, lat: number, lng: number) => {
    // Use a Suncalc-style algorithm (well-tested) for robust sun position calculations
    const rad = Math.PI / 180;
    const dayMs = 1000 * 60 * 60 * 24;
    const J1970 = 2440588;
    const J2000 = 2451545;

    const toJulian = (d: Date) => d.getTime() / dayMs - 0.5 + J1970;
    const toDays = (d: Date) => toJulian(d) - J2000;

    const e = rad * 23.4397; // obliquity

    const d = toDays(date);
    // mean anomaly of the Sun
    const M = rad * (357.5291 + 0.98560028 * d);
    // mean longitude
    const C = rad * (1.9148 * Math.sin(M) + 0.0200 * Math.sin(2 * M) + 0.0003 * Math.sin(3 * M));
    const P = rad * 102.9372; // perihelion of the Earth
    const L = M + C + P + Math.PI;

    const dec = Math.asin(Math.sin(L) * Math.sin(e));
    const RA = Math.atan2(Math.sin(L) * Math.cos(e), Math.cos(L));

    const lw = lng * rad;
    const phi = lat * rad;

    // sidereal time
    const GMST = rad * (280.16 + 360.9856235 * d) - Math.PI * 2 * Math.floor((280.16 + 360.9856235 * d) / 360);
    const LST = GMST + lw;

    const H = LST - RA;
    const altitude = Math.asin(Math.sin(phi) * Math.sin(dec) + Math.cos(phi) * Math.cos(dec) * Math.cos(H));
    const az = Math.atan2(Math.sin(H), Math.cos(H) * Math.sin(phi) - Math.tan(dec) * Math.cos(phi));

    // convert azimuth to radians from north, clockwise
    const azimuth = (az + Math.PI) % (Math.PI * 2);
    return { azimuth, altitude };
  };

  // Timezone-aware sampled sunrise/sunset using the precise sunPosition
  const sampleSunriseSunset = (date: Date, lat: number, lng: number) => {
    const tzOffsetMin = date.getTimezoneOffset();
    const baseLocalUtc =
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0) + tzOffsetMin * 60 * 1000;

    // sample the 24-hour day at 5-minute intervals to find first/last altitude > threshold
    const samples = 288; // 24*12 => 5-minute steps
    const stepHours = 24 / samples;
    let sunrise: number | null = null;
    let sunset: number | null = null;
    let prevAlt: number | null = null;
    let prevT = 0;
    // small positive altitude threshold to avoid numerical noise
    const ALT_THRESHOLD = 0.0005; // ~0.0286 degrees
    for (let i = 0; i <= samples; i++) {
      const t = i * stepHours;
      const minutes = Math.round(t * 60);
      const d = new Date(baseLocalUtc + minutes * 60 * 1000);
      const sp = sunPosition(d, lat, lng);
      const alt = sp.altitude;
      if (prevAlt !== null) {
        if (prevAlt <= ALT_THRESHOLD && alt > ALT_THRESHOLD && sunrise === null) {
          const frac = prevAlt === alt ? 0 : (ALT_THRESHOLD - prevAlt) / (alt - prevAlt);
          sunrise = prevT + (t - prevT) * frac;
        }
        if (prevAlt > ALT_THRESHOLD && alt <= ALT_THRESHOLD && sunset === null) {
          const frac = prevAlt === alt ? 0 : (prevAlt - ALT_THRESHOLD) / (prevAlt - alt);
          sunset = prevT + (t - prevT) * frac;
        }
      }
      prevAlt = alt;
      prevT = t;
    }

    // if either not found, try a fallback: sample at 1-minute resolution (more expensive)
    if (sunrise === null || sunset === null) {
      const samples2 = 1440; // 24*60 => 1-minute steps
      prevAlt = null;
      prevT = 0;
      for (let i = 0; i <= samples2 && (sunrise === null || sunset === null); i++) {
        const t = i / 60; // hours
        const minutes = Math.round(t * 60);
        const d = new Date(baseLocalUtc + minutes * 60 * 1000);
        const sp = sunPosition(d, lat, lng);
        const alt = sp.altitude;
        if (prevAlt !== null) {
          if (sunrise === null && prevAlt <= ALT_THRESHOLD && alt > ALT_THRESHOLD) {
            const frac = prevAlt === alt ? 0 : (ALT_THRESHOLD - prevAlt) / (alt - prevAlt);
            sunrise = prevT + (t - prevT) * frac;
          }
          if (sunset === null && prevAlt > ALT_THRESHOLD && alt <= ALT_THRESHOLD) {
            const frac = prevAlt === alt ? 0 : (prevAlt - ALT_THRESHOLD) / (prevAlt - alt);
            sunset = prevT + (t - prevT) * frac;
          }
        }
        prevAlt = alt;
        prevT = t;
      }
    }

    // If still not found, return defaults (6-18)
    if (sunrise === null) sunrise = 6;
    if (sunset === null) sunset = 18;
    return normalizeSunWindow(sunrise, sunset);
  };

  // Use sampled method (timezone-aware) as the canonical sunrise/sunset, proven to match projection results
  const computeSunriseSunset = useCallback(
    (date: Date, lat: number, lng: number) => sampleSunriseSunset(date, lat, lng),
    [sampleSunriseSunset]
  );

  const updateShadeWindow = useCallback(() => {
    const map = getMap();
    if (!map) return;
    try {
      const times = computeSunriseSunset(shadeDate, map.getCenter().lat, map.getCenter().lng);
      let sunrise = times.sunrise;
      let sunset = times.sunset;
      setShadeMinHour(sunrise);
      setShadeMaxHour(sunset);
      // Clamp current shadeHour strictly into [sunrise, sunset] window
      setShadeHour((prev) => {
        if (Number.isFinite(prev) && prev >= sunrise && prev <= sunset) {
          return prev;
        }
        // if previous was outside, snap to sunrise to keep slider aligned
        return sunrise;
      });
    } catch {
      // ignore; keep defaults
    }
  }, [computeSunriseSunset, getMap, shadeDate]);

  // Ensure slider bounds reflect computed sunrise/sunset
  useEffect(() => {
    updateShadeWindow();
  }, [updateShadeWindow]);

  // Keep shade window fresh when shade is toggled on
  useEffect(() => {
    if (shadeEnabled) {
      updateShadeWindow();
    }
  }, [shadeEnabled, updateShadeWindow]);

  useEffect(() => {
    return () => {
      if (shadeRafRef.current) {
        cancelAnimationFrame(shadeRafRef.current);
      }
    };
  }, []);

  const metersToLatLngOffset = (lat: number, dx: number, dy: number) => {
    const R = 6378137;
    const dLat = dy / R;
    const dLng = dx / (R * Math.cos((Math.PI * lat) / 180));
    return { dLat: dLat * (180 / Math.PI), dLng: dLng * (180 / Math.PI) };
  };

  const buildShadowPolygons = useCallback(
    (buildings: any[], sun: { azimuth: number; altitude: number }) => {
    const map = getMap();
    if (!map || !shadowLayerRef.current) return;
    shadowLayerRef.current.clearLayers();
    if (sun.altitude <= 0) return;
    // altitude is in radians; compute shadow length in meters
  const tanAlt = Math.tan(sun.altitude || 0);
  const az = sun.azimuth; // radians from north, clockwise

  // unit sun vector: east = sin(az), north = cos(az)
  const sunUnitEast = Math.sin(az);
  const sunUnitNorth = Math.cos(az);

    // debug: log sun and building count
    // eslint-disable-next-line no-console
    console.debug("buildShadowPolygons: sun", { az: sun.azimuth, alt: sun.altitude, buildings: buildings.length });

    buildings.forEach((b, bi) => {
      const height = (b.height ?? b.h ?? 10) as number;
      const shadowLen = Math.max(0, height / Math.max(Math.abs(tanAlt), 0.000001));
      if (shadowLen <= 0.1) return;

      // shadow shift (in meters) = -shadowLen * sunUnit (shadows fall opposite the sun)
      let shiftEast = -shadowLen * sunUnitEast;
      let shiftNorth = -shadowLen * sunUnitNorth;
      // (no manual flip) shadows always use sun vector direction

      // debug: log first building metrics
      if (bi === 0) {
        // eslint-disable-next-line no-console
        console.debug("shadow metrics", { height, shadowLen, shiftEast, shiftNorth });
      }

      const pts = b.points as LatLng[];
      if (!pts || pts.length < 3) return;

      // compute shifted points (each vertex moved by the shadow vector)
      const shifted: LatLng[] = pts.map((p) => {
        const offset = metersToLatLngOffset(p.lat, shiftEast, shiftNorth);
        return L.latLng(p.lat + offset.dLat, p.lng + offset.dLng);
      });

      // Merge base + shifted into a single hull to avoid overlap seams (restores previous filled look)
      const merged = computeConvexHull([...pts, ...shifted]);
      L.polygon(merged, {
        pane: "shadePane",
        stroke: false,
        fillColor: "#000",
        fillOpacity: 1,
        interactive: false,
      }).addTo(shadowLayerRef.current as L.LayerGroup);
    });
  },
  [getMap]
);

  const fetchBuildingsForBounds = useCallback(
    async (bounds: LatLngBounds) => {
      const south = bounds.getSouth();
      const west = bounds.getWest();
      const north = bounds.getNorth();
      const east = bounds.getEast();
      const query = `[out:json][timeout:20];
      (
        way["building"](${south},${west},${north},${east});
      );
      out geom tags;`;
      const url = `https://overpass-api.de/api/interpreter?data=${encodeURIComponent(query)}`;
      try {
        const res = await fetch(url);
        if (!res.ok) return [];
        const data = await res.json();
        if (!data?.elements) return [];
        const buildings = data.elements
          .map((el: any) => {
            if (!el.geometry || el.geometry.length < 3) return null;
            const pts = el.geometry.map((g: any) => L.latLng(g.lat, g.lon));
            const heightTag = parseFloat(el.tags?.height);
            const levels = parseFloat(el.tags?.["building:levels"]);
            const height = !Number.isNaN(heightTag)
              ? heightTag
              : !Number.isNaN(levels)
              ? levels * 3
              : 10;
            return { points: pts, height };
          })
          .filter(Boolean);
        return buildings as any[];
      } catch {
        return [];
      }
    },
    []
  );

  const updateMapAddress = useCallback(
    async (latlng: LatLng) => {
      setMapCoords(`${latlng.lat.toFixed(5)}, ${latlng.lng.toFixed(5)}`);
      const fallback = `${latlng.lat.toFixed(5)}, ${latlng.lng.toFixed(5)}`;
      const nominatimUrl = `${NOMINATIM_BASE}/reverse?format=jsonv2&lat=${latlng.lat}&lon=${latlng.lng}`;
      const data = await fetchJsonWithTimeout(nominatimUrl);
      if (data) {
        const formatted = formatAddress(data);
        if (formatted) {
          setMapAddress(formatted);
          return;
        }
      }
      const photonUrl = `${PHOTON_BASE}/reverse?lat=${latlng.lat}&lon=${latlng.lng}`;
      const photon = await fetchJsonWithTimeout(photonUrl);
      const feature = photon?.features?.[0];
      const props = feature?.properties;
      if (props) {
        const parts = [
          props.name,
          [props.housenumber, props.street].filter(Boolean).join(" ").trim(),
          props.city || props.town || props.village,
          props.state,
          props.country,
        ].filter(Boolean);
        if (parts.length) {
          setMapAddress(parts.join(", "));
          return;
        }
      }
      setMapAddress(fallback);
    },
    []
  );

  const deselectLots = () => {
    applySelection(new Set(), null);
  };

  const selectLot = useCallback((lotId: string, additive = false, toggle = false) => {
    const next = additive ? new Set(selectedLotIdsRef.current) : new Set<string>();
    if (additive) {
      if (toggle && next.has(lotId)) {
        next.delete(lotId);
      } else {
        next.add(lotId);
      }
    } else {
      next.add(lotId);
    }
    const primary = next.size ? (next.has(lotId) ? lotId : firstOfSet(next)) : null;
    applySelection(next, primary);
  }, [applySelection]);

  const focusLotSelection = (lotId: string) => {
    const next = selectedLotIdsRef.current.size ? new Set(selectedLotIdsRef.current) : new Set<string>();
    if (!next.has(lotId)) {
      next.clear();
      next.add(lotId);
    }
    applySelection(next, lotId);
  };

  const createLotLabel = (latlng: LatLng, id: string) => {
    const el = document.createElement("div");
    el.className = "lot-label";
    el.textContent = id;

    const marker = L.marker(latlng, {
      icon: L.divIcon({ html: el, className: "", iconSize: undefined }),
      interactive: true,
    }).addTo(getMap() as L.Map);

    return { marker, element: el };
  };

  const clearExistingLots = () => {
    const map = getMap();
    if (!map) return;
    lotsRef.current.forEach((lot) => {
      map.removeLayer(lot.polygon);
      map.removeLayer(lot.labelMarker);
      clearLotExtras(lot);
    });
    lotsRef.current = [];
    applySelection(new Set(), null);
    setLotCount(0);
    bumpAccessVersion();
  };

  const updateZoomStatus = useCallback(() => {
    const map = getMap();
    if (!map) return;
    const z = map.getZoom();
    const zoomOk = z >= 16;
    setZoomReady(zoomOk);
    if (!zoomOk) {
      setStatusMessage("Zoom in closer before auto-detecting lots", "warn");
    } else {
      setStatusMessage("", "ready");
    }
  }, [getMap, setStatusMessage]);

  const disableShadows = useCallback(() => {
    const map = getMap();
    const layer = shadowLayerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    shadowBuildingsRef.current = [];
    if (map.hasLayer(layer)) {
      map.removeLayer(layer);
    }
    setShadeEnabled(false);
  }, [getMap]);

  const refreshShadows = useCallback(async () => {
    const map = getMap();
    if (!map || !shadeEnabled) return;
    if (!shadowLayerRef.current) return;
    if (shadowLoading) return;
    const zoom = map.getZoom();
    if (zoom < MIN_SHADOW_ZOOM) {
      disableShadows();
      // If we were already showing shadows, indicate they turned off due to zoom. Otherwise prompt to zoom in.
      const message = shadeEnabled ? "Zoomed too far out for shadows" : "Zoom in to enable shadows";
      setStatusMessage(message, "warn", 2000);
      return;
    }
    setShadowLoading(true);
    setStatusMessage("Calculating shadows… Don't move!", "warn", 3000);
    const center = map.getCenter();
  const sunDate = new Date(shadeDate);
  sunDate.setHours(0, 0, 0, 0);
  // support fractional shadeHour (minutes) and preserve overflow without wrapping (keeps slider end aligned)
  const totalMinutes = Math.round(shadeHour * 60);
  sunDate.setMinutes(totalMinutes, 0, 0);
  let sun = sunPosition(sunDate, center.lat, center.lng);
    // debug: log time and sun info
    // eslint-disable-next-line no-console
    console.debug("refreshShadows: time", { shadeHour, date: sunDate.toISOString(), sun });

    // Avoid extremely small/negative altitudes within the daylight window to prevent jumps
    const MIN_ALT_RAD = 0.001; // ~0.057 degrees
    if (shadeHour >= shadeMinHour && shadeHour <= shadeMaxHour && sun.altitude < MIN_ALT_RAD) {
      sun.altitude = MIN_ALT_RAD;
    }
    const last = shadowLastStateRef.current;
    const distanceMoved =
      last.center && last.zoom !== null ? map.distance(center, last.center) : Number.POSITIVE_INFINITY;
    const zoomChanged = last.zoom !== null && Math.abs(map.getZoom() - last.zoom) >= 1;
    try {
      const needFetch = !shadowBuildingsRef.current.length || distanceMoved > 400 || zoomChanged;
      const canFetch = !shadowCooldownRef.current;

      if (needFetch) {
        if (!canFetch) {
          // We're in a short cooldown for heavy fetches. If we already have buildings data, continue and
          // just rebuild shadows; otherwise politely skip fetch and leave current shadows as-is.
          if (!shadowBuildingsRef.current.length) {
            const currentStatus = statusRef.current;
            const shadowWarn =
              currentStatus.tone === "warn" &&
              (currentStatus.text.includes("Zoom in to enable shadows") ||
                currentStatus.text.includes("Zoomed too far out for shadows"));
            if (!shadowWarn) {
              setStatusMessage("Calculating shadows… Don't move!", "warn", 800);
            }
            setShadowLoading(false);
            return;
          }
        } else {
          const bounds = map.getBounds().pad(0.05);
          const widthMeters = map.distance(bounds.getSouthWest(), bounds.getSouthEast());
          const heightMeters = map.distance(bounds.getSouthWest(), bounds.getNorthWest());
          if (widthMeters > 2000 || heightMeters > 2000) {
            shadowBuildingsRef.current = [];
            setShadowLoading(false);
            shadowCooldownRef.current = window.setTimeout(() => (shadowCooldownRef.current = null), 800);
            return;
          }
          const buildings = await fetchBuildingsForBounds(bounds);
          if (buildings.length > 400) {
            shadowBuildingsRef.current = buildings.slice(0, 400);
            setStatusMessage("Shadows limited to first 400 buildings in view", "warn", 2500);
          } else {
            shadowBuildingsRef.current = buildings;
          }
          shadowLastStateRef.current = { center, zoom: map.getZoom() };
          // after a heavy fetch set a short cooldown to avoid repeated expensive calls
          shadowCooldownRef.current = window.setTimeout(() => (shadowCooldownRef.current = null), 800);
        }
      } else {
        shadowLastStateRef.current = { center, zoom: map.getZoom() };
      }

      // Rebuild shadow geometry for current sun position (cheap if buildings already loaded)
      buildShadowPolygons(shadowBuildingsRef.current, sun);
      setStatusMessage("Shadows enabled", "ready", 1000);
      shadeJustEnabledRef.current = false;
    } catch (err) {
      console.error("Shadow render failed", err);
      setStatusMessage("Shadow rendering failed", "error");
    } finally {
      setShadowLoading(false);
      shadowCooldownRef.current = window.setTimeout(() => {
        shadowCooldownRef.current = null;
      }, 1000);
    }
  }, [
    MIN_SHADOW_ZOOM,
    buildShadowPolygons,
    disableShadows,
    fetchBuildingsForBounds,
    getMap,
    shadeEnabled,
    shadeHour,
    sunPosition,
    shadowLoading,
    shadeDate,
    shadeMinHour,
    shadeMaxHour,
    computeSunriseSunset,
  ]);

  const geocode = async (query: string): Promise<{ lat: number; lon: number } | null> => {
    const nominatimUrl = `${NOMINATIM_BASE}/search?format=jsonv2&q=${encodeURIComponent(query)}`;
    const data = await fetchJsonWithTimeout(nominatimUrl);
    if (Array.isArray(data) && data.length) {
      const lat = parseFloat(data[0].lat);
      const lon = parseFloat(data[0].lon);
      if (Number.isFinite(lat) && Number.isFinite(lon)) {
        return { lat, lon };
      }
    }
    const photonUrl = `${PHOTON_BASE}/api/?q=${encodeURIComponent(query)}&limit=1`;
    const photon = await fetchJsonWithTimeout(photonUrl);
    if (photon?.features?.length) {
      const coords = photon.features[0].geometry?.coordinates;
      if (Array.isArray(coords) && coords.length >= 2) {
        return { lat: coords[1], lon: coords[0] };
      }
    }
    return null;
  };

  const fetchSuggestions = useCallback(async (query: string): Promise<Suggestion[]> => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return [];
    }
    const nominatimUrl = `${NOMINATIM_BASE}/search?format=jsonv2&limit=8&q=${encodeURIComponent(query)}`;
    const nominatim = await fetchJsonWithTimeout(nominatimUrl);
    let suggestionsList: Suggestion[] = [];
    if (Array.isArray(nominatim) && nominatim.length) {
      suggestionsList = nominatim
        .map((d: any) => ({
          name: d.display_name as string,
          lat: parseFloat(d.lat),
          lon: parseFloat(d.lon),
        }))
        .filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lon));
    } else {
      const photonUrl = `${PHOTON_BASE}/api/?q=${encodeURIComponent(query)}&limit=8`;
      const photon = await fetchJsonWithTimeout(photonUrl);
      if (photon?.features?.length) {
        suggestionsList = photon.features
          .map((f: any) => ({
            name: f.properties?.name || f.properties?.street || "Result",
            lat: f.geometry?.coordinates?.[1],
            lon: f.geometry?.coordinates?.[0],
          }))
          .filter((s: Suggestion) => Number.isFinite(s.lat) && Number.isFinite(s.lon));
      }
    }
    setSuggestions(suggestionsList);
    setShowSuggestions(suggestionsList.length > 0);
    return suggestionsList;
  }, []);

  const detectLotsFromOSM = async (): Promise<any[]> => {
    const map = getMap();
    if (!map) return [];
    return detectLotsWithinBounds(map.getBounds());
  };

  const getPolygonPoints = (polygon: L.Polygon): LatLng[] => {
    const latlngs = polygon.getLatLngs();
    if (!latlngs.length) return [];
    if (Array.isArray(latlngs[0])) return latlngs[0] as LatLng[];
    return latlngs as LatLng[];
  };

  // Safely get the first value of a Set<string> as string | null (iterator.value can be undefined)
  const firstOfSet = (s: Set<string>): string | null => {
    const it = s.values().next();
    return it.done ? null : it.value;
  };

  const pointInPolygon = (point: LatLng, vs: LatLng[]): boolean => {
    const x = point.lat;
    const y = point.lng;
    let inside = false;
    for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
      const xi = vs[i].lat;
      const yi = vs[i].lng;
      const xj = vs[j].lat;
      const yj = vs[j].lng;
      const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / ((yj - yi) || 1e-12) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  };

  const getAccessJitter = (lot: Lot) => {
    const total = lot.entrances.length + lot.exits.length;
    const angle = (total % 10) * (Math.PI / 5);
    const radius = 0.00004 + (total % 3) * 0.00001; // a few meters in latitude/longitude
    return {
      lat: Math.cos(angle) * radius,
      lng: Math.sin(angle) * radius,
    };
  };

  const fetchRoadsForBounds = async (bounds: LatLngBounds): Promise<any[]> => {
    const clampBounds = (b: LatLngBounds) => {
      const latSpan = b.getNorth() - b.getSouth();
      const lngSpan = b.getEast() - b.getWest();
      const maxSpan = 0.02; // ~2km cap to avoid giant queries
      if (latSpan <= maxSpan && lngSpan <= maxSpan) return b;
      const center = b.getCenter();
      const halfLat = maxSpan / 2;
      const halfLng = maxSpan / 2;
      return L.latLngBounds(
        [center.lat - halfLat, center.lng - halfLng],
        [center.lat + halfLat, center.lng + halfLng]
      );
    };
    const limited = clampBounds(bounds);
    const s = limited.getSouth();
    const w = limited.getWest();
    const n = limited.getNorth();
    const e = limited.getEast();
    const overpassQuery = `
[out:json][timeout:25];
(
  way["highway"]["highway"!~"footway|path|cycleway|bridleway"](${s},${w},${n},${e});
);
out geom;`;
    const url = `https://overpass-api.de/api/interpreter?data=${encodeURIComponent(overpassQuery)}`;
    const res = await fetch(url);
    if (!res.ok) return [];
    const data = await res.json();
    return data.elements || [];
  };

  const makeEntranceIcon = (type: AccessType, rotation: number) => {
    const shell = document.createElement("div");
    shell.className = "access-marker-shell";
    const img = document.createElement("img");
    img.className = "access-marker-img";
    img.src =
      type === "both"
        ? "/assets/mapper/enter_exit.svg"
        : type === "exit"
        ? "/assets/mapper/exit.svg"
        : "/assets/mapper/enter.svg";
    img.alt = type;
    img.style.transform = `rotate(${rotation}deg)`;
    shell.appendChild(img);
    return L.divIcon({
      html: shell,
      className: "",
      iconSize: [32, 32],
      iconAnchor: [16, 16],
    });
  };

  const createAccessMarker = (
    lot: Lot,
    latlng: LatLng,
    initialType: AccessType,
    initialRotation = 0
  ): AccessEntry => {
    const map = getMap();
    if (!map) throw new Error("Map not ready");

    const marker = L.marker(latlng, {
      draggable: true,
      icon: makeEntranceIcon(initialType, 0),
    }).addTo(map);

    const entry: AccessEntry = {
      layer: marker,
      type: initialType,
      rotation: initialRotation,
      setType: () => {},
      remove: () => {},
    };

    const removeFromCollections = () => {
      if (rotatingAccessRef.current === entry) {
        stopAccessRotation();
      }
      lot.entrances = lot.entrances.filter((e) => e !== entry);
      lot.exits = lot.exits.filter((e) => e !== entry);
    };

    const attachToLot = (type: AccessType) => {
      removeFromCollections();
      entry.type = type;
      if (type === "entrance") {
        lot.entrances.push(entry);
      } else if (type === "exit") {
        lot.exits.push(entry);
      } else {
        lot.entrances.push(entry);
        lot.exits.push(entry);
      }
      marker.setIcon(makeEntranceIcon(type, entry.rotation));
      bumpAccessVersion();
    };

    entry.setType = attachToLot;
    entry.remove = () => {
      removeFromCollections();
      map.removeLayer(marker);
      if (hoveredAccessRef.current === entry) {
        hoveredAccessRef.current = null;
      }
      bumpAccessVersion();
    };

    attachToLot(initialType);

    marker.on("dragstart", () => {
      if (rotatingAccessRef.current === entry) {
        stopAccessRotation();
      }
      map.dragging.disable();
    });

    marker.on("drag", (e) => {
      const oe = (e as any).originalEvent as MouseEvent | undefined;
      if (oe) {
        lastPointerRef.current = { x: oe.clientX, y: oe.clientY };
      }
      setShowDeleteHint(isOverSidebar(lastPointerRef.current));
    });

    marker.on("dragend", () => {
      map.dragging.enable();
      setShowDeleteHint(false);
      if (isOverSidebar(lastPointerRef.current)) {
        entry.remove();
      }
    });

    marker.on("dblclick", (e) => {
      e.originalEvent.preventDefault();
      if (rotatingAccessRef.current === entry) {
        stopAccessRotation();
      }
      setAccessRotation(entry, entry.rotation + 180);
      marker.setIcon(makeEntranceIcon(entry.type, entry.rotation));
    });

    marker.on("mouseover", () => {
      hoveredAccessRef.current = entry;
    });

    marker.on("mouseout", () => {
      if (hoveredAccessRef.current === entry) {
        hoveredAccessRef.current = null;
      }
    });

    marker.on("contextmenu", (evt: L.LeafletMouseEvent) => {
      evt.originalEvent.preventDefault();
      evt.originalEvent.stopPropagation();
      const clientX = (evt.originalEvent as MouseEvent).clientX;
      const clientY = (evt.originalEvent as MouseEvent).clientY;
      const items: MenuItem[] = [];
      if (entry.type !== "entrance") {
        items.push({
          key: "set-entrance",
          label: withKeyHint("Set Enter", "Arrow"),
          icon: menuIcon("/assets/mapper/enter.svg", "Set entrance"),
          onSelect: () => attachToLot("entrance"),
        });
      }
      if (entry.type !== "exit") {
        items.push({
          key: "set-exit",
          label: withKeyHint("Set Exit", "Arrow"),
          icon: menuIcon("/assets/mapper/exit.svg", "Set exit"),
          onSelect: () => attachToLot("exit"),
        });
      }
      if (entry.type !== "both") {
        items.push({
          key: "set-both",
          label: withKeyHint("Set Both", "Arrow"),
          icon: menuIcon("/assets/mapper/enter_exit.svg", "Set both"),
          onSelect: () => attachToLot("both"),
        });
      }
      items.push({
        key: "rotate",
        label: withKeyHint("Rotate", "R"),
        icon: menuIcon("/assets/mapper/rotate.svg", "Rotate"),
        onSelect: () => {
          closeContextMenu();
          startAccessRotation(entry);
        },
      });
      items.push({
        key: "delete-access",
        label: withKeyHint("Delete", "Del"),
        icon: menuIcon("/assets/mapper/trash.svg", "Delete"),
        tone: "danger",
        onSelect: () => {
          removeFromCollections();
          map.removeLayer(marker);
          bumpAccessVersion();
        },
      });
      openContextMenu(items, clientX, clientY);
    });

    return entry;
  };

  const autoDetectAccessForLot = async (lot: Lot) => {
    try {
      const map = getMap();
      if (!map) return;
      const bounds = lot.polygon.getBounds();
      const roads = await fetchRoadsForBounds(bounds);
      const polyPoints = getPolygonPoints(lot.polygon);
      if (!polyPoints.length) return;

      const center = bounds.getCenter();
      const angleBetween = (a: LatLng, b: LatLng) => {
        const pa = map.latLngToLayerPoint(a);
        const pb = map.latLngToLayerPoint(b);
        const dx = pb.x - pa.x;
        const dy = pb.y - pa.y;
        const deg = (Math.atan2(dy, dx) * 180) / Math.PI;
        return deg + 90; // icon faces up by default
      };
      const distanceToPolygon = (latlng: LatLng) => {
        const pts = getPolygonPoints(lot.polygon);
        if (pts.length < 2) return Number.POSITIVE_INFINITY;
        let best = Number.POSITIVE_INFINITY;
        for (let i = 0; i < pts.length; i++) {
          const j = (i + 1) % pts.length;
          const a = map.latLngToLayerPoint(pts[i]);
          const b = map.latLngToLayerPoint(pts[j]);
          const p = map.latLngToLayerPoint(latlng);
          const dist = L.LineUtil.pointToSegmentDistance(p, a, b);
          if (dist < best) best = dist;
        }
        return best;
      };

      const candidates: { latlng: LatLng; type: AccessType; rotation?: number }[] = [];

      roads.forEach((el) => {
        const g = el.geometry;
        if (!g || g.length < 2) return;
        const oneway = (el.tags && el.tags.oneway) || "";
        const isOneWay =
          oneway === "yes" || oneway === "1" || oneway === "true" || oneway === "-1";
        const isReverse = oneway === "-1";
        for (let i = 0; i < g.length - 1; i++) {
          const p1 = L.latLng(g[i].lat, g[i].lon);
          const p2 = L.latLng(g[i + 1].lat, g[i + 1].lon);
          const inside1 = pointInPolygon(p1, polyPoints);
          const inside2 = pointInPolygon(p2, polyPoints);
          if (inside1 === inside2) continue;
          const mid = L.latLng((p1.lat + p2.lat) / 2, (p1.lng + p2.lng) / 2);
          const angle = angleBetween(p1, p2);
          let type: AccessType = "both";
          if (isOneWay) {
            const leaving = inside1 && !inside2;
            const dir = isReverse ? !leaving : leaving;
            type = dir ? "exit" : "entrance";
          }
          const edgeDistancePx = distanceToPolygon(mid);
          const maxDistancePx = 22;
          if (edgeDistancePx > maxDistancePx) return;
          candidates.push({ latlng: mid, type, rotation: angle });
        }
      });

      const unique: { latlng: LatLng; type: AccessType; rotation?: number }[] = [];
      candidates.forEach((c) => {
        const found = unique.find((u) => map.distance(u.latlng, c.latlng) < 8);
        if (!found) {
          unique.push(c);
        } else if (found && found.type !== c.type) {
          found.type = "both";
          if (!found.rotation && c.rotation !== undefined) {
            found.rotation = c.rotation;
          }
        } else if (!found.rotation && c.rotation !== undefined) {
          found.rotation = c.rotation;
        }
      });

      unique.forEach((c) => {
        let rotation = c.rotation;
        if (rotation === undefined) {
          const toward = angleBetween(c.latlng, center);
          rotation = c.type === "exit" ? toward + 180 : toward;
        }
        createAccessMarker(lot, c.latlng, c.type, rotation);
      });

      if (!unique.length) {
        // Fallback: drop four bi-directional points at polygon bounds midpoints
        const b = lot.polygon.getBounds();
        const midTop = L.latLng(b.getNorth(), b.getWest() + (b.getEast() - b.getWest()) / 2);
        const midBottom = L.latLng(b.getSouth(), b.getWest() + (b.getEast() - b.getWest()) / 2);
        const midLeft = L.latLng(b.getSouth() + (b.getNorth() - b.getSouth()) / 2, b.getWest());
        const midRight = L.latLng(b.getSouth() + (b.getNorth() - b.getSouth()) / 2, b.getEast());
        [midTop, midBottom, midLeft, midRight].forEach((pt) => {
          const rot = angleBetween(pt, center);
          createAccessMarker(lot, pt, "both", rot);
        });
      }
    } catch (err) {
      console.error("autoDetectAccessForLot failed", err);
    }
  };

  const reDetectLot = useCallback(
    async (lot: Lot) => {
      const map = getMap();
      if (!map) return;
      const bounds = lot.polygon.getBounds().pad(0.0015);
      setStatusMessage(`Re-detecting ${lot.id}…`, "warn");
      try {
        const elements = await detectLotsWithinBounds(bounds);
        const center = lot.polygon.getBounds().getCenter();
        const toLatLngs = (el: any): LatLng[] =>
          Array.isArray(el.geometry) ? el.geometry.map((g: any) => L.latLng(g.lat, g.lon)) : [];

        let chosen: LatLng[] | null = null;
        for (const el of elements) {
          const coords = toLatLngs(el);
          if (coords.length >= 3 && pointInPolygon(center, coords)) {
            chosen = coords;
            break;
          }
        }
        if (!chosen && elements.length) {
          const coords = toLatLngs(elements[0]);
          if (coords.length >= 3) chosen = coords;
        }
        if (!chosen) {
          const b = lot.polygon.getBounds();
          chosen = [
            b.getSouthWest(),
            L.latLng(b.getSouth(), b.getEast()),
            b.getNorthEast(),
            L.latLng(b.getNorth(), b.getWest()),
          ];
        }
        resetLotGeometry(lot, chosen);
        await autoDetectAccessForLot(lot);
        selectLot(lot.id);
        setStatusMessage(`Re-detected ${lot.id}`, "ready");
      } catch (err) {
        console.error("reDetectLot failed", err);
        setStatusMessage("Re-detect failed for this lot", "error");
      }
    },
    [autoDetectAccessForLot, detectLotsWithinBounds, getMap, pointInPolygon, resetLotGeometry, selectLot, setStatusMessage]
  );

  const removeLot = useCallback((lotId: string) => {
    const map = getMap();
    if (!map) return;
    const idx = lotsRef.current.findIndex((l) => l.id === lotId);
    if (idx === -1) return;
    const lot = lotsRef.current[idx];
    if (hoveredLotRef.current?.id === lotId) hoveredLotRef.current = null;
    if (hoveredEdgeRef.current?.lot.id === lotId) hoveredEdgeRef.current = null;
    if (hoveredHandleRef.current?.lot.id === lotId) hoveredHandleRef.current = null;
    map.removeLayer(lot.polygon);
    map.removeLayer(lot.labelMarker);
    clearLotExtras(lot);
    lotsRef.current.splice(idx, 1);
    const nextSelected = new Set(selectedLotIdsRef.current);
    nextSelected.delete(lotId);
    const nextPrimary = nextSelected.size
      ? nextSelected.has(selectedLotIdRef.current || "")
        ? (selectedLotIdRef.current ?? null)
        : firstOfSet(nextSelected)
      : null;
    applySelection(nextSelected, nextPrimary);
    setLotCount(lotsRef.current.length);
    bumpAccessVersion();
  }, [applySelection, bumpAccessVersion, clearLotExtras, getMap]);

  const removeSelectedLots = useCallback(() => {
    const ids = Array.from(selectedLotIdsRef.current);
    ids.forEach((id) => removeLot(id));
  }, [removeLot]);

  const computeConvexHull = (points: LatLng[]): LatLng[] => {
    const unique = points.reduce<LatLng[]>((acc, pt) => {
      if (!acc.some((p) => p.lat === pt.lat && p.lng === pt.lng)) acc.push(pt);
      return acc;
    }, []);
    if (unique.length <= 3) return unique;
    const sorted = [...unique].sort((a, b) => (a.lng === b.lng ? a.lat - b.lat : a.lng - b.lng));
    const cross = (o: LatLng, a: LatLng, b: LatLng) =>
      (a.lat - o.lat) * (b.lng - o.lng) - (a.lng - o.lng) * (b.lat - o.lat);
    const lower: LatLng[] = [];
    sorted.forEach((p) => {
      while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
        lower.pop();
      }
      lower.push(p);
    });
    const upper: LatLng[] = [];
    for (let i = sorted.length - 1; i >= 0; i--) {
      const p = sorted[i];
      while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
        upper.pop();
      }
      upper.push(p);
    }
    upper.pop();
    lower.pop();
    return lower.concat(upper);
  };

  const mergeSelectedLots = useCallback(() => {
    const selectedLots = lotsRef.current.filter((l) => selectedLotIdsRef.current.has(l.id));
    const count = selectedLots.length;
    if (count < 2) {
      setStatusMessage("Select at least two lots to merge", "warn");
      return;
    }
    const allPoints = selectedLots.flatMap((l) => getPolygonPoints(l.polygon));
    if (allPoints.length < 3) {
      setStatusMessage("Not enough points to merge these lots", "warn");
      return;
    }
    const merged = computeConvexHull(allPoints);
    const base = selectedLots[0];
    applyLotGeometry(base, merged, true);
    selectedLots.slice(1).forEach((lot) => removeLot(lot.id));
    applySelection(new Set([base.id]), base.id);
    setStatusMessage(`Merged ${count} lots`, "ready");
  }, [applyLotGeometry, applySelection, getPolygonPoints, removeLot, setStatusMessage]);

  const commitLotRename = useCallback(
    (lot: Lot, nextId: string) => {
      const current = lot.id;
      const trimmed = nextId.trim();
      if (!trimmed || trimmed === current) {
        lot.labelElement.textContent = current;
        return;
      }
      lot.id = trimmed;
      lot.labelElement.textContent = trimmed;
      const updatedSelected = new Set(
        Array.from(selectedLotIdsRef.current).map((id) => (id === current ? trimmed : id))
      );
      const primary =
        selectedLotIdRef.current === current
          ? trimmed
          : updatedSelected.has(selectedLotIdRef.current || "")
          ? (selectedLotIdRef.current ?? null)
          : updatedSelected.size
          ? firstOfSet(updatedSelected)
          : null;
      applySelection(updatedSelected, primary);
      setStatusMessage(`Renamed to ${trimmed}`, "ready");
    },
    [applySelection, setStatusMessage]
  );

  const startInlineRename = useCallback(
    (lot: Lot) => {
      const el = lot.labelElement;
      if (!el || el.isContentEditable) return;
      const original = lot.id;
      el.contentEditable = "true";
      el.classList.add("lot-editing");
      const selectAll = () => {
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        if (!sel) return;
        sel.removeAllRanges();
        sel.addRange(range);
      };
      selectAll();
      el.focus();

      const finish = (commit: boolean) => {
        el.contentEditable = "false";
        el.classList.remove("lot-editing");
        el.removeEventListener("blur", onBlur);
        el.removeEventListener("keydown", onKeyDown as any);
        const text = (el.textContent || "").trim();
        if (commit) {
          commitLotRename(lot, text || original);
        } else {
          el.textContent = original;
        }
      };

      const onBlur = () => finish(true);
      const onKeyDown = (evt: KeyboardEvent) => {
        if (evt.key === "Enter") {
          evt.preventDefault();
          finish(true);
        } else if (evt.key === "Escape") {
          evt.preventDefault();
          finish(false);
        }
      };

      el.addEventListener("blur", onBlur);
      el.addEventListener("keydown", onKeyDown as any);
    },
    [commitLotRename]
  );

  const openLotMenu = useCallback(
    (lot: Lot, clientX: number, clientY: number) => {
      openContextMenu(
        [
          {
            key: "redetect-lot",
            label: "Re-detect Lot",
            icon: menuIcon("/assets/mapper/redetect.svg", "Re-detect"),
            onSelect: () => {
              closeContextMenu();
              void reDetectLot(lot);
            },
          },
          {
            key: "rename-lot",
            label: "Rename Lot",
            icon: menuIcon("/assets/mapper/rename.svg", "Rename"),
            onSelect: () => {
              closeContextMenu();
              startInlineRename(lot);
            },
          },
          {
            key: "delete-lot",
            label: withKeyHint("Delete Lot", "Del"),
            icon: menuIcon("/assets/mapper/trash.svg", "Delete"),
            tone: "danger",
            onSelect: () => {
              closeContextMenu();
              removeLot(lot.id);
            },
          },
        ],
        clientX,
        clientY
      );
    },
    [closeContextMenu, openContextMenu, reDetectLot, removeLot, startInlineRename]
  );

  const openMultiLotMenu = useCallback(
    (clientX: number, clientY: number) => {
      const count = selectedLotIdsRef.current.size;
      if (count < 2) return;
      const labelSuffix = count === 1 ? "Lot" : "Lots";
      openContextMenu(
        [
          {
            key: "delete-lots",
            label: withKeyHint(`Delete ${count} ${labelSuffix}`, "Del"),
            icon: menuIcon("/assets/mapper/trash.svg", "Delete"),
            tone: "danger",
            onSelect: () => {
              closeContextMenu();
              removeSelectedLots();
            },
          },
          {
            key: "merge-lots",
            label: withKeyHint(`Merge ${count} ${labelSuffix}`, "M"),
            icon: menuIcon("/assets/mapper/merge.svg", "Merge"),
            onSelect: () => {
              closeContextMenu();
              mergeSelectedLots();
            },
          },
        ],
        clientX,
        clientY
      );
    },
    [closeContextMenu, mergeSelectedLots, openContextMenu, removeSelectedLots]
  );

  const attachLotInteractions = (lot: Lot) => {
    lot.polygon.on("click", (evt: L.LeafletMouseEvent) => {
      const mouse = evt.originalEvent as MouseEvent | undefined;
      selectLot(lot.id, Boolean(mouse?.shiftKey), true);
    });
    lot.polygon.on("mouseover", () => {
      hoveredLotRef.current = lot;
    });
    lot.polygon.on("mouseout", () => {
      if (hoveredLotRef.current === lot) hoveredLotRef.current = null;
      if (hoveredEdgeRef.current?.lot === lot) hoveredEdgeRef.current = null;
    });
    lot.polygon.on("mousemove", (evt: L.LeafletMouseEvent) => {
      const map = getMap();
      if (!map) return;
      const pts = getPolygonPoints(lot.polygon);
      if (pts.length < 2) return;
      const cursor = map.latLngToLayerPoint(evt.latlng);
      let best: { latlng: LatLng; dist: number } | null = null;
      for (let i = 0; i < pts.length; i++) {
        const j = (i + 1) % pts.length;
        const a = map.latLngToLayerPoint(pts[i]);
        const b = map.latLngToLayerPoint(pts[j]);
        const dist = L.LineUtil.pointToSegmentDistance(cursor, a, b);
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const denom = dx * dx + dy * dy || 1;
        let t = ((cursor.x - a.x) * dx + (cursor.y - a.y) * dy) / denom;
        t = Math.min(1, Math.max(0, t));
        const proj = L.point(a.x + dx * t, a.y + dy * t);
        const latlng = map.layerPointToLatLng(proj);
        if (!best || dist < best.dist) {
          best = { latlng, dist };
        }
      }
      const thresholdPx = 12;
      if (best && best.dist <= thresholdPx) {
        hoveredEdgeRef.current = { lot, latlng: best.latlng };
      } else if (hoveredEdgeRef.current?.lot === lot) {
        hoveredEdgeRef.current = null;
      }
    });
    lot.labelMarker.on("click", (evt: L.LeafletMouseEvent) => {
      const mouse = evt.originalEvent as MouseEvent | undefined;
      selectLot(lot.id, Boolean(mouse?.shiftKey), true);
    });
    lot.labelMarker.on("mouseover", () => {
      hoveredLotRef.current = lot;
    });
    lot.labelMarker.on("mouseout", () => {
      if (hoveredLotRef.current === lot) hoveredLotRef.current = null;
    });
    lot.labelMarker.on("dblclick", (evt: L.LeafletMouseEvent) => {
      evt.originalEvent.preventDefault();
      evt.originalEvent.stopPropagation();
      startInlineRename(lot);
    });
    lot.labelMarker.on("contextmenu", (evt: L.LeafletMouseEvent) => {
      evt.originalEvent.preventDefault();
      evt.originalEvent.stopPropagation();
      if (selectedLotIdsRef.current.has(lot.id)) {
        focusLotSelection(lot.id);
      } else {
        selectLot(lot.id);
      }
      const clientX = (evt.originalEvent as MouseEvent).clientX;
      const clientY = (evt.originalEvent as MouseEvent).clientY;
      if (selectedLotIdsRef.current.size > 1) {
        openMultiLotMenu(clientX, clientY);
      } else {
        openLotMenu(lot, clientX, clientY);
      }
    });
    lot.polygon.on("contextmenu", (evt: L.LeafletMouseEvent) => {
      evt.originalEvent.preventDefault();
      evt.originalEvent.stopPropagation();
      const map = getMap();
      if (!map) return;
      const pts = getPolygonPoints(lot.polygon);
      if (pts.length < 2) return;

      if (selectedLotIdsRef.current.has(lot.id)) {
        focusLotSelection(lot.id);
      } else {
        selectLot(lot.id);
      }
      if (selectedLotIdsRef.current.size > 1) {
        const clientXMulti = (evt.originalEvent as MouseEvent).clientX;
        const clientYMulti = (evt.originalEvent as MouseEvent).clientY;
        openMultiLotMenu(clientXMulti, clientYMulti);
        return;
      }

      // Only show the add-point menu when the click is near an edge; otherwise lot menu
      const clickPt = map.latLngToLayerPoint(evt.latlng);
      const thresholdPx = 14;
      let minDist = Number.POSITIVE_INFINITY;
      for (let i = 0; i < pts.length; i++) {
        const j = (i + 1) % pts.length;
        const a = map.latLngToLayerPoint(pts[i]);
        const b = map.latLngToLayerPoint(pts[j]);
        const dist = L.LineUtil.pointToSegmentDistance(clickPt, a, b);
        if (dist < minDist) minDist = dist;
      }
      const clientX = (evt.originalEvent as MouseEvent).clientX;
      const clientY = (evt.originalEvent as MouseEvent).clientY;
      if (minDist <= thresholdPx) {
        focusLotSelection(lot.id);
        openContextMenu(
          [
            {
              key: "add-point",
              label: withKeyHint("Add Point", "A"),
              icon: menuIcon("/assets/mapper/add-handle.svg", "Add point"),
              onSelect: () => {
                insertPointIntoLot(lot, evt.latlng);
                closeContextMenu();
              },
            },
          ],
          clientX,
          clientY
        );
      } else {
        focusLotSelection(lot.id);
        openLotMenu(lot, clientX, clientY);
      }
    });
  };

  const serializeMapper = useCallback(() => {
    const lots = lotsRef.current.map((lot) => {
      const points = getPolygonPoints(lot.polygon).map((p) => ({ lat: p.lat, lng: p.lng }));
      const accessSet = new Set<AccessEntry>([...lot.entrances, ...lot.exits]);
      const accesses = Array.from(accessSet).map((a) => {
        const ll = a.layer.getLatLng();
        return { lat: ll.lat, lng: ll.lng, type: a.type, rotation: a.rotation ?? 0 };
      });
      return { id: lot.id, points, accesses };
    });
    return {
      version: 1,
      generatedAt: new Date().toISOString(),
      lots,
    };
  }, [getPolygonPoints]);

  const downloadMapper = useCallback(() => {
    const data = serializeMapper();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const link = document.createElement("a");
    const safeName = sanitizeFilename(mapAddress);
    link.download = `${safeName}.mapper`;
    link.href = URL.createObjectURL(blob);
    link.click();
    URL.revokeObjectURL(link.href);
    setStatusMessage("Exported .mapper file", "ready");
  }, [mapAddress, sanitizeFilename, serializeMapper, setStatusMessage]);

  const importMapperData = useCallback(
    (payload: any) => {
      const map = getMap();
      if (!map) return;
      if (!payload || payload.version !== 1 || !Array.isArray(payload.lots)) {
        setStatusMessage("Invalid .mapper file", "error");
        return;
      }
      const createdIds: string[] = [];
      payload.lots.forEach((l: any) => {
        if (!l || !Array.isArray(l.points) || l.points.length < 3) return;
        const coords = l.points
          .map((p: any) => (typeof p?.lat === "number" && typeof p?.lng === "number" ? L.latLng(p.lat, p.lng) : null))
          .filter(Boolean) as LatLng[];
        if (coords.length < 3) return;
        const id = typeof l.id === "string" && l.id.trim() ? l.id.trim() : `Lot ${lotCounterRef.current++}`;
        const poly = L.polygon(coords, makeLotStyle(false)).addTo(map);
        const center = poly.getBounds().getCenter();
        const label = createLotLabel(center, id);
        const lot: Lot = {
          id,
          polygon: poly,
          labelMarker: label.marker,
          labelElement: label.element,
          entrances: [],
          exits: [],
          islands: [],
          handles: [],
        };
        attachLotInteractions(lot);
        lotsRef.current.push(lot);
        if (Array.isArray(l.accesses)) {
          l.accesses.forEach((a: any) => {
            if (typeof a?.lat !== "number" || typeof a?.lng !== "number") return;
            const type: AccessType = a.type === "exit" ? "exit" : a.type === "entrance" ? "entrance" : "both";
            const rotation = typeof a?.rotation === "number" ? a.rotation : 0;
            createAccessMarker(lot, L.latLng(a.lat, a.lng), type, rotation);
          });
        }
        createdIds.push(id);
      });
      if (!createdIds.length) {
        setStatusMessage("No lots were imported", "warn");
        return;
      }
      const firstId = createdIds[0];
      applySelection(new Set([firstId]), firstId);
      const lot = lotsRef.current.find((l) => l.id === firstId);
      if (lot) {
        map.setView(lot.polygon.getBounds().getCenter(), Math.max(map.getZoom(), 18));
      }
      setLotCount(lotsRef.current.length);
      setStatusMessage(`Imported ${createdIds.length} lot${createdIds.length > 1 ? "s" : ""}`, "ready");
    },
    [applySelection, attachLotInteractions, createAccessMarker, createLotLabel, getMap, setStatusMessage]
  );

  const handleImportFile = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const parsed = JSON.parse(String(reader.result));
          importMapperData(parsed);
        } catch (err) {
          console.error("Import failed", err);
          setStatusMessage("Import failed — invalid file", "error");
        }
      };
      reader.readAsText(file);
    },
    [importMapperData, setStatusMessage]
  );

  const finalizeDraftLot = useCallback((stayInAddMode = false) => {
    const map = getMap();
    if (!map) return;
    const pts = addLotPointsRef.current;
    if (pts.length < 3) {
      setStatusMessage("Add at least 3 points before closing the lot", "warn");
      return;
    }
    const id = `Lot ${lotCounterRef.current++}`;
    const closingSeg = L.polyline([pts[pts.length - 1], pts[0]], { color: "#1782ff", weight: 2 }).addTo(map);
    addLotSegmentsRef.current.push(closingSeg);
    const poly = L.polygon(pts, makeLotStyle(false)).addTo(map);
    const center = poly.getBounds().getCenter();
    const label = createLotLabel(center, id);
    const lot: Lot = {
      id,
      polygon: poly,
      labelMarker: label.marker,
      labelElement: label.element,
      entrances: [],
      exits: [],
      islands: [],
      handles: [],
    };
    attachLotInteractions(lot);
    lotsRef.current.push(lot);
    applySelection(new Set([id]), id);
    setLotCount(lotsRef.current.length);
    void autoDetectAccessForLot(lot);
    clearAddLotDraft();
    if (stayInAddMode) {
      addLotModeRef.current = true;
      setAddLotMode(true);
      setStatusMessage(`${id} created — continue adding points`, "ready");
    } else {
      addLotModeRef.current = false;
      setAddLotMode(false);
      setStatusMessage(`Created ${id}`, "ready");
    }
    setAddLotPointsVersion((v) => v + 1);
  }, [
    applySelection,
    attachLotInteractions,
    autoDetectAccessForLot,
    clearAddLotDraft,
    createLotLabel,
    getMap,
    setStatusMessage,
  ]);

  const buildLotsFromElements = (elements: any[]) => {
    const map = getMap();
    if (!map) return;
    clearExistingLots();
    elements.forEach((el) => {
      if (!el.geometry || el.geometry.length < 3) return;
      const coords = el.geometry.map((g: { lat: number; lon: number }) => L.latLng(g.lat, g.lon));
      const id = `Lot ${lotCounterRef.current++}`;
      const poly = L.polygon(coords, makeLotStyle(false)).addTo(map);
      const center = poly.getBounds().getCenter();
      const label = createLotLabel(center, id);
      const lot: Lot = {
        id,
        polygon: poly,
        labelMarker: label.marker,
        labelElement: label.element,
        entrances: [],
        exits: [],
        islands: [],
        handles: [],
      };
      attachLotInteractions(lot);
      lotsRef.current.push(lot);
      void autoDetectAccessForLot(lot);
    });
    if (!selectedLotIdsRef.current.size && lotsRef.current.length) {
      const firstId = lotsRef.current[0].id;
      applySelection(new Set([firstId]), firstId);
    }
    setLotCount(lotsRef.current.length);
  };

  const fallbackHeuristicLots = () => {
    const map = getMap();
    if (!map) return;
    clearExistingLots();
    const bounds = map.getBounds();
    const center = bounds.getCenter();
    const latSpan = bounds.getNorth() - bounds.getSouth();
    const lngSpan = bounds.getEast() - bounds.getWest();

    const halfLat = latSpan * 0.12;
    const halfLng = lngSpan * 0.12;
    const sw = L.latLng(center.lat - halfLat, center.lng - halfLng);
    const ne = L.latLng(center.lat + halfLat, center.lng + halfLng);

    const id = `Lot ${lotCounterRef.current++}`;
    const poly = L.polygon(
      [sw, L.latLng(sw.lat, ne.lng), ne, L.latLng(ne.lat, sw.lng)],
      makeLotStyle(false)
    ).addTo(map);

    const label = createLotLabel(poly.getBounds().getCenter(), id);
    const lot: Lot = {
      id,
      polygon: poly,
      labelMarker: label.marker,
      labelElement: label.element,
      entrances: [],
      exits: [],
      islands: [],
      handles: [],
    };
    attachLotInteractions(lot);
    lotsRef.current.push(lot);
    void autoDetectAccessForLot(lot);
    if (!selectedLotIdsRef.current.size) {
      applySelection(new Set([id]), id);
    }
    setLotCount(lotsRef.current.length);
  };

  const getSelectedLot = useCallback((): Lot | null => {
    const id = selectedLotIdRef.current;
    if (!id) return null;
    return lotsRef.current.find((l) => l.id === id) ?? null;
  }, []);

  const getActiveLot = useCallback(() => {
    const selected = getSelectedLot();
    if (selected) return selected;
    return lotsRef.current[0] ?? null;
  }, [getSelectedLot]);

  const addEntranceExit = (
    lot: Lot,
    latlng: LatLng,
    type: AccessType,
    rotation = 0
  ): AccessEntry | null => {
    const map = getMap();
    if (map) {
      const tooClose = lot.entrances.concat(lot.exits).some((e) => map.distance(e.layer.getLatLng(), latlng) < 3);
      if (tooClose) return null;
    }
    return createAccessMarker(lot, latlng, type, rotation);
  };

  const placeAccessAtCenter = useCallback(
    (type: AccessType) => {
      const lot = getActiveLot();
      if (!lot) {
        setStatusMessage("Detect a lot before adding entrances/exits", "warn");
        return;
      }
      const center = lot.polygon.getBounds().getCenter();
      const jitter = getAccessJitter(lot);
      const target = L.latLng(center.lat + jitter.lat, center.lng + jitter.lng);
      const placed = addEntranceExit(lot, target, type, 0);
      if (placed) {
        setPlacementType(type);
        setMode("select");
        const label = type === "both" ? "entrance/exit" : type;
        setStatusMessage(`Added ${label} at the lot center`, "ready");
      }
    },
    [addEntranceExit, getActiveLot, setStatusMessage]
  );

  const placeAccessFromPalette = useCallback(
    (type: AccessType, clientX: number, clientY: number) => {
      const map = getMap();
      const lot = getActiveLot();
      if (!map || !lot) {
        setStatusMessage("Detect a lot before adding entrances/exits", "warn");
        return;
      }
      const mapRect = getMapRect();
      if (!mapRect) return;
      const inside =
        clientX >= mapRect.left && clientX <= mapRect.right && clientY >= mapRect.top && clientY <= mapRect.bottom;
      if (!inside) return;
      const containerPoint = L.point(clientX - mapRect.left, clientY - mapRect.top);
      const latlng = map.containerPointToLatLng(containerPoint);
      const placed = addEntranceExit(lot, latlng, type, 0);
      if (placed) {
        setPlacementType(type);
        const label = type === "both" ? "entrance/exit" : type;
        setStatusMessage(`Placed ${label} on the map`, "ready");
      }
    },
    [addEntranceExit, getActiveLot, getMap, getMapRect, setStatusMessage]
  );

  const cancelPalettePreDrag = useCallback(() => {
    palettePendingDragRef.current = null;
    if (palettePreDragMoveRef.current) {
      window.removeEventListener("pointermove", palettePreDragMoveRef.current);
      palettePreDragMoveRef.current = null;
    }
    if (palettePreDragUpRef.current) {
      window.removeEventListener("pointerup", palettePreDragUpRef.current);
      palettePreDragUpRef.current = null;
    }
    setShowDeleteHint(false);
  }, []);

  const stopPaletteDrag = useCallback(() => {
    cancelPalettePreDrag();
    if (paletteMoveHandlerRef.current) {
      window.removeEventListener("pointermove", paletteMoveHandlerRef.current);
      paletteMoveHandlerRef.current = null;
    }
    if (paletteUpHandlerRef.current) {
      window.removeEventListener("pointerup", paletteUpHandlerRef.current);
      paletteUpHandlerRef.current = null;
    }
    paletteDragTypeRef.current = null;
    setPaletteDrag(null);
    setShowDeleteHint(false);
  }, [cancelPalettePreDrag]);

  const startPaletteDrag = (type: AccessType, start: { x: number; y: number }) => {
    cancelPalettePreDrag();
    const map = getMap();
    const lot = getActiveLot();
    if (!map || !lot) {
      setStatusMessage("Detect a lot before adding entrances/exits", "warn");
      return;
    }
    setStatusMessage("Drag the icon onto the map to place it", "warn");
    paletteDragTypeRef.current = type;
    setPaletteDrag({ type, x: start.x, y: start.y });
    const move = (evt: PointerEvent) => {
      lastPointerRef.current = { x: evt.clientX, y: evt.clientY };
      setPaletteDrag((prev) => (prev ? { ...prev, x: evt.clientX, y: evt.clientY } : prev));
      setShowDeleteHint(isOverSidebar(lastPointerRef.current));
    };
    const up = (evt: PointerEvent) => {
      if (paletteDragTypeRef.current) {
        placeAccessFromPalette(paletteDragTypeRef.current, evt.clientX, evt.clientY);
      }
      suppressNextClickRef.current = evt.target instanceof HTMLButtonElement;
      stopPaletteDrag();
    };
    paletteMoveHandlerRef.current = move;
    paletteUpHandlerRef.current = up;
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const handleControlPointerDown = (type: AccessType, e: React.PointerEvent<HTMLButtonElement>) => {
    if (!lotCount) return;
    cancelPalettePreDrag();
    palettePendingDragRef.current = { type, startX: e.clientX, startY: e.clientY };
    const moveHandler = (evt: PointerEvent) => {
      const pending = palettePendingDragRef.current;
      if (!pending || paletteDragTypeRef.current) return;
      const dx = Math.abs(evt.clientX - pending.startX);
      const dy = Math.abs(evt.clientY - pending.startY);
      if (Math.max(dx, dy) > 4) {
        cancelPalettePreDrag();
        startPaletteDrag(pending.type, { x: evt.clientX, y: evt.clientY });
      }
    };
    palettePreDragMoveRef.current = moveHandler;
    window.addEventListener("pointermove", moveHandler);
    const upHandler = () => {
      cancelPalettePreDrag();
    };
    palettePreDragUpRef.current = upHandler;
    window.addEventListener("pointerup", upHandler);
  };

  const handleControlPointerUp = () => {
    cancelPalettePreDrag();
  };

  const handleControlPointerLeave = () => {
    cancelPalettePreDrag();
  };

  const handleControlClick = (type: AccessType) => {
    if (!lotCount) {
      setStatusMessage("Detect a lot before adding entrances/exits", "warn");
      return;
    }
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false;
      return;
    }
    placeAccessAtCenter(type);
  };

  const addIsland = useCallback((lot: Lot, latlng: LatLng) => {
    const map = getMap();
    if (!map) return;
    const spanLat = (map.getBounds().getNorth() - map.getBounds().getSouth()) * 0.06;
    const spanLng = (map.getBounds().getEast() - map.getBounds().getWest()) * 0.06;
    const sw = L.latLng(latlng.lat - spanLat / 2, latlng.lng - spanLng / 2);
    const ne = L.latLng(latlng.lat + spanLat / 2, latlng.lng + spanLng / 2);
    const rect = L.rectangle(L.latLngBounds(sw, ne), {
      color: "#facc15",
      weight: 2,
      fillColor: "#facc15",
      fillOpacity: 0.65,
    }).addTo(map);

    const bounds = rect.getBounds();
    dragIslandDimsRef.current = {
      latSpan: bounds.getNorth() - bounds.getSouth(),
      lngSpan: bounds.getEast() - bounds.getWest(),
    };

    rect.on("mousedown", (e) => {
      if (modeRef.current !== "select") return;
      draggingIslandRef.current = rect;
      const b = rect.getBounds();
      dragIslandDimsRef.current = {
        latSpan: b.getNorth() - b.getSouth(),
        lngSpan: b.getEast() - b.getWest(),
      };
      dragIslandOffsetRef.current = {
        lat: b.getCenter().lat - e.latlng.lat,
        lng: b.getCenter().lng - e.latlng.lng,
      };
      map.dragging.disable();
    });

    lot.islands.push({ layer: rect });
    bumpAccessVersion();
  }, [bumpAccessVersion, getMap]);

  const handleMapClick = useCallback(
    (e: LeafletMouseEvent) => {
      closeContextMenu();
      if (addLotModeRef.current) {
        addDraftLotPoint(e.latlng);
        return;
      }
      const lot = getSelectedLot();
      if (!lot) return;
      const activeMode = modeRef.current;
      if (activeMode === "addIsland") {
        addIsland(lot, e.latlng);
      }
    },
    [addDraftLotPoint, addIsland, closeContextMenu, getSelectedLot]
  );

  const handleMouseMove = useCallback((e: LeafletMouseEvent) => {
    const map = getMap();
    if (!map) return;
    const oe = (e as any).originalEvent as MouseEvent | undefined;
    if (oe) {
      lastPointerRef.current = { x: oe.clientX, y: oe.clientY };
    }
    const draggingIsland = draggingIslandRef.current;
    const dims = dragIslandDimsRef.current;
    const offset = dragIslandOffsetRef.current;
    if (draggingIsland && dims && offset) {
      const centerLat = e.latlng.lat + offset.lat;
      const centerLng = e.latlng.lng + offset.lng;
      const halfLat = dims.latSpan / 2;
      const halfLng = dims.lngSpan / 2;
      const sw = L.latLng(centerLat - halfLat, centerLng - halfLng);
      const ne = L.latLng(centerLat + halfLat, centerLng + halfLng);
      draggingIsland.setBounds(L.latLngBounds(sw, ne));
    }

  }, [getMap]);

  const handleMouseUp = useCallback(
    (e: LeafletMouseEvent) => {
      const map = getMap();
      if (!map) return;
      const isRightButton = e?.originalEvent instanceof MouseEvent && e.originalEvent.button === 2;
      if (!isRightButton) {
        closeContextMenu();
      }
      if (draggingIslandRef.current) {
        draggingIslandRef.current = null;
        dragIslandOffsetRef.current = null;
        dragIslandDimsRef.current = null;
        map.dragging.enable();
      }
      setShowDeleteHint(false);
    },
    [closeContextMenu, getMap]
  );

  const handleIdentifyLots = useCallback(async () => {
    const map = getMap();
    if (!map) return;
    const z = map.getZoom();
    if (z < 17) {
      updateZoomStatus();
      return;
    }

    setIsDetecting(true);
    try {
      setStatusMessage("Detecting parking lots…", "warn");
      const elements = await detectLotsFromOSM();
      if (elements.length) {
        buildLotsFromElements(elements);
        setStatusMessage(`Detected ${elements.length} lots`, "ready");
      } else {
        fallbackHeuristicLots();
        setStatusMessage("No mapped lots found — created a draft lot", "warn");
      }
    } catch (err) {
      console.error(err);
      fallbackHeuristicLots();
      setStatusMessage("Detection failed — created a draft lot", "error");
    } finally {
      setIsDetecting(false);
    }
  }, [buildLotsFromElements, detectLotsFromOSM, fallbackHeuristicLots, getMap, setStatusMessage, updateZoomStatus]);

  const handleDeleteLot = () => {
    if (!selectedLotIdRef.current) return;
    removeLot(selectedLotIdRef.current);
  };

  const handleLocate = () => {
    const map = getMap();
    if (!map || !navigator.geolocation) {
      setStatusMessage("Geolocation unavailable", "warn");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        map.setView([latitude, longitude], Math.max(map.getZoom(), 18));
      },
      () => setStatusMessage("Could not access location", "error"),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 8000 }
    );
  };

  const toggleAddLotMode = useCallback(() => {
    if (addLotModeRef.current) {
      const pts = addLotPointsRef.current;
      if (pts.length >= 3) {
        finalizeDraftLot();
      } else {
        cancelAddLotDraft();
      }
      return;
    }
    addLotModeRef.current = true;
    setAddLotMode(true);
    clearAddLotDraft();
    setStatusMessage("Add lot: click to place points, right-click to close", "warn");
  }, [cancelAddLotDraft, clearAddLotDraft, finalizeDraftLot, setStatusMessage]);

  const handleSuggestionSelect = (s: { name: string; lat: number; lon: number }) => {
    setSearchQuery(s.name);
    setShowSuggestions(false);
    const map = getMap();
    if (map) {
      map.setView([s.lat, s.lon], 19);
    }
  };

  const handleSearch = useCallback(async () => {
    const map = getMap();
    if (!map) return;
    const query = searchQuery.trim();
    if (!query) return;
    try {
      setStatusMessage("Searching…", "warn");
      let res: Suggestion | null = suggestions[0] ?? null;
      if (!res) {
        const fetched = await fetchSuggestions(query);
        res = fetched && fetched.length ? fetched[0] : null;
      }
      if (!res) {
        const geocodeRes = await geocode(query);
        if (geocodeRes) {
          res = { ...geocodeRes, name: query };
        }
      }
      if (!res) {
        setStatusMessage("Nothing found for that search", "error");
        return;
      }
      map.setView([res.lat, res.lon], 19);
      setShowSuggestions(false);
    } catch (err) {
      console.error(err);
      setStatusMessage("Search failed. Try again.", "error");
    }
  }, [fetchSuggestions, geocode, getMap, searchQuery, setStatusMessage, suggestions]);

  useEffect(() => {
    const trackPointer = (e: PointerEvent) => {
      lastPointerRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener("pointermove", trackPointer);
    return () => window.removeEventListener("pointermove", trackPointer);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
        return;
      }
      const access = hoveredAccessRef.current;
      if (access) {
        if (e.key.toLowerCase() === "r") {
          e.preventDefault();
          if (rotatingAccessRef.current === access) {
            stopAccessRotation({ text: "Rotation set", tone: "ready" });
          } else {
            startAccessRotation(access);
          }
          return;
        }
        if (e.key === "Delete") {
          e.preventDefault();
          access.remove();
          return;
        }
        if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
          e.preventDefault();
          const order: AccessType[] = ["both", "entrance", "exit"];
          const idx = order.indexOf(access.type);
          const delta = e.key === "ArrowRight" ? 1 : -1;
          const next = order[(idx + delta + order.length) % order.length];
          access.setType(next);
          return;
        }
      }

      if (e.key === "Delete") {
        const handle = hoveredHandleRef.current;
        if (handle) {
          e.preventDefault();
          const pts = getPolygonPoints(handle.lot.polygon);
          if (pts.length <= 3) {
            setStatusMessage("A lot needs at least 3 points", "warn");
            return;
          }
          const next = pts.filter((_, i) => i !== handle.index);
          handle.lot.polygon.setLatLngs(next);
          handle.lot.polygon.redraw();
          updateLotLabelPosition(handle.lot);
          refreshLotHandles(handle.lot);
          return;
        }

        const lot = hoveredLotRef.current;
        if (lot) {
          e.preventDefault();
          removeLot(lot.id);
          return;
        }
      }

      if (e.key.toLowerCase() === "a") {
        const edge = hoveredEdgeRef.current;
        if (edge) {
          e.preventDefault();
          insertPointIntoLot(edge.lot, edge.latlng);
        }
        return;
      }

      if (e.key.toLowerCase() === "m") {
        if (selectedLotIdsRef.current.size > 1) {
          e.preventDefault();
          mergeSelectedLots();
        }
        return;
      }

      if (addLotModeRef.current) {
        if (e.key === "Enter") {
          e.preventDefault();
          finalizeDraftLot(true);
          return;
        }
        if (e.key === "Escape") {
          e.preventDefault();
          cancelAddLotDraft();
          return;
        }
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        if (addLotModeRef.current) {
          e.preventDefault();
          undoDraftLotPoint();
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [
    insertPointIntoLot,
    mergeSelectedLots,
    removeLot,
    refreshLotHandles,
    startAccessRotation,
    stopAccessRotation,
    finalizeDraftLot,
    cancelAddLotDraft,
    undoDraftLotPoint,
    updateLotLabelPosition,
    setStatusMessage,
  ]);

  useEffect(() => {
    return () => {
      stopAccessRotation();
    };
  }, [stopAccessRotation]);

  useEffect(() => {
    return () => {
      stopPaletteDrag();
    };
  }, [stopPaletteDrag]);

  useEffect(() => {
    return () => {
      if (statusTimerRef.current) window.clearTimeout(statusTimerRef.current);
      if (statusClearRef.current) window.clearTimeout(statusClearRef.current);
    };
  }, []);

  useEffect(() => {
    const map = getMap();
    if (!map) return;
    // Nudge Leaflet to recalc tiles after sidebar resize/collapse
    map.invalidateSize();
    const t = window.setTimeout(() => map.invalidateSize(), 150);
    return () => window.clearTimeout(t);
  }, [getMap, sidebarCollapsed]);

  useEffect(() => {
    return () => {
      clearAddLotDraft();
    };
  }, [clearAddLotDraft]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      void fetchSuggestions(searchQuery.trim());
    }, 350);
    return () => window.clearTimeout(t);
  }, [searchQuery, fetchSuggestions]);

  useEffect(() => {
    if (!shadeEnabled) return;
    if (shadeRafRef.current) {
      cancelAnimationFrame(shadeRafRef.current);
    }
    shadeRafRef.current = requestAnimationFrame(() => {
      shadeRafRef.current = null;
      void refreshShadows();
    });
  }, [shadeHour, shadeEnabled, shadeDate, refreshShadows]);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    try {
      const startCenter: [number, number] = initialStoredViewRef.current
        ? [initialStoredViewRef.current.lat, initialStoredViewRef.current.lng]
        : DEFAULT_VIEW;
      const startZoom = initialStoredViewRef.current?.zoom ?? 18;
      const map = L.map(mapContainerRef.current, {
        zoomControl: false,
        attributionControl: false,
        preferCanvas: true,
        renderer: L.canvas({ tolerance: 8 }),
      }).setView(startCenter, startZoom);
      mapRef.current = map;
      map.createPane("shadePane");
      const shadePane = map.getPane("shadePane");
      if (shadePane) {
        shadePane.classList.add("shadow-layer");
        shadePane.style.opacity = "0.5"; // ensure all shadows are composited at 50% as a single layer
        shadePane.style.mixBlendMode = "multiply";
        shadePane.style.pointerEvents = "none";
      }
      // Custom zoom control in top-right with themed buttons
      const ZoomControl = L.Control.extend({
        onAdd: (m: L.Map) => {
          const container = L.DomUtil.create("div", "mapper-zoom-control leaflet-bar");
          const btnIn = L.DomUtil.create("button", "mapper-zoom-btn zoom-in", container);
          btnIn.type = "button";
          const imgIn = document.createElement("img");
          imgIn.src = "/assets/mapper/plus.svg";
          imgIn.alt = "Zoom in";
          btnIn.appendChild(imgIn);

          const btnOut = L.DomUtil.create("button", "mapper-zoom-btn zoom-out", container);
          btnOut.type = "button";
          const imgOut = document.createElement("img");
          imgOut.src = "/assets/mapper/minus.svg";
          imgOut.alt = "Zoom out";
          btnOut.appendChild(imgOut);

          const update = () => {
            const z = m.getZoom();
            btnIn.disabled = z >= m.getMaxZoom();
            btnOut.disabled = z <= m.getMinZoom();
          };

          L.DomEvent.on(btnIn, "click", (e) => {
            L.DomEvent.stopPropagation(e);
            m.zoomIn();
          });
          L.DomEvent.on(btnOut, "click", (e) => {
            L.DomEvent.stopPropagation(e);
            m.zoomOut();
          });

          m.on("zoomend", update);
          update();
          return container;
        },
        onRemove: (m: L.Map) => {
          m.off("zoomend");
        },
      });
      new ZoomControl({ position: "topright" }).addTo(map);
      map.whenReady(() => {
        map.invalidateSize();
        setTimeout(() => map.invalidateSize(), 150);
        updateMapAddress(map.getCenter());
        updateShadeWindow();
        if (shadeEnabled && shadowLayerRef.current) {
          shadowLayerRef.current.addTo(map);
          void refreshShadows();
        }
      });

      // Use CDN-hosted marker assets to avoid bundling image imports
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
        iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
        shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
      });

      shadowLayerRef.current = L.layerGroup([], { pane: "shadePane" });

      const esri = L.tileLayer(
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        {
          maxZoom: 21,
          attribution:
            "Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community",
          crossOrigin: true,
        }
      );
      const carto = L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
        maxZoom: 20,
        attribution: "© OpenStreetMap contributors © CARTO",
        subdomains: "abcd",
        crossOrigin: true,
      });
      baseLayersRef.current = { esri, carto };

      // Offline grid as a last-resort visual so the map never looks empty
      const OfflineGrid: any = (L.GridLayer as any).extend({
        createTile(coords: L.Coords) {
          const tile = document.createElement("canvas");
          tile.width = 256;
          tile.height = 256;
          const ctx = tile.getContext("2d");
          if (!ctx) return tile;
          ctx.fillStyle = "#0b1220";
          ctx.fillRect(0, 0, 256, 256);
          ctx.strokeStyle = "rgba(23, 130, 255, 0.25)";
          ctx.lineWidth = 1;
          for (let i = 0; i <= 256; i += 64) {
            ctx.beginPath();
            ctx.moveTo(i, 0);
            ctx.lineTo(i, 256);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(0, i);
            ctx.lineTo(256, i);
            ctx.stroke();
          }
          ctx.fillStyle = "rgba(255,255,255,0.35)";
          ctx.font = "11px sans-serif";
          ctx.fillText(`z${coords.z} / ${coords.x},${coords.y}`, 8, 16);
          return tile;
        },
      });
      const offlineGrid: L.GridLayer = new OfflineGrid({
        attribution: "Parallel offline grid",
        tileSize: 256,
        keepBuffer: 4,
      });

      const setBaseLayer = (next: "esri" | "carto") => {
        const desired = baseLayersRef.current[next];
        const mapInstance = mapRef.current;
        if (!desired || !mapInstance) return;
        const prev = baseLayersRef.current[activeBaseRef.current];
        if (prev && mapInstance.hasLayer(prev)) {
          prev.off("tileerror");
          prev.off("load");
          prev.off("loading");
          mapInstance.removeLayer(prev);
        }
        desired.on("tileerror", () => {
          // auto-switch to the other base if current fails
          const fallback = next === "esri" ? "carto" : "esri";
          setStatusMessage(`Base layer failed — switching to ${fallback}`, "warn");
          setBaseLayer(fallback);
        });
        desired.on("loading", () => {
          setStatusMessage("Loading…", "warn", 5000);
        });
        desired.on("load", () => {
          const currentStatus = statusRef.current;
          const isShadowWarn =
            currentStatus.tone === "warn" &&
            (currentStatus.text.includes("Zoomed too far out for shadows") ||
              currentStatus.text.includes("Zoom in to enable shadows"));
          if (!isShadowWarn) {
            setStatusMessage("Loading complete", "ready", 1200);
          }
          if (mapInstance.hasLayer(offlineGrid)) {
            mapInstance.removeLayer(offlineGrid);
          }
        });
        desired.addTo(mapInstance);
        activeBaseRef.current = next;
        setActiveBase(next);
        if (mapContainerRef.current) {
          mapContainerRef.current.classList.toggle("carto-base", next === "carto");
          mapContainerRef.current.classList.toggle("esri-base", next === "esri");
        }
        persistCurrentView();
      };

      switchBaseRef.current = setBaseLayer;

      setBaseLayer(activeBaseRef.current);

      map.on("zoomend", updateZoomStatus);
      map.on("mousemove", handleMouseMove);
      map.on("mouseup", handleMouseUp);
      map.on("click", handleMapClick);
      map.on("moveend", () => {
        if (addressTimerRef.current) {
          window.clearTimeout(addressTimerRef.current);
        }
        addressTimerRef.current = window.setTimeout(() => {
          updateMapAddress(map.getCenter());
        }, 400);
        updateShadeWindow();
        if (shadeEnabled) {
          void refreshShadows();
        }
        persistCurrentView();
      });
      const ctxHandler = (e: L.LeafletMouseEvent) => {
        e.originalEvent.preventDefault();
        if (addLotModeRef.current) {
          finalizeDraftLot();
        }
      };
      map.on("contextmenu", ctxHandler);

      updateZoomStatus();

      return () => {
        map.off("zoomend", updateZoomStatus);
        map.off("mousemove", handleMouseMove);
        map.off("mouseup", handleMouseUp);
        map.off("click", handleMapClick);
        map.off("moveend");
        map.off("contextmenu", ctxHandler);
        const { esri, carto } = baseLayersRef.current;
        [esri, carto].forEach((layer) => {
          if (layer && map.hasLayer(layer)) {
            map.removeLayer(layer);
          }
        });
        if (shadowLayerRef.current && map.hasLayer(shadowLayerRef.current)) {
          map.removeLayer(shadowLayerRef.current);
        }
        map.remove();
        mapRef.current = null; // allow re-init in StrictMode double-invoke
      };
    } catch (err) {
      console.error("Mapper init failed", err);
      setStatusMessage("Map initialization failed. See console for details.", "error");
    }
  }, [handleMapClick, handleMouseMove, handleMouseUp, persistCurrentView, updateZoomStatus]);

  const statusClass = `status ${status.tone}`;
  const shadeControlsVisible = shadeEnabled && !shadowLoading;
  const addLotHelp =
    addLotMode && addLotPointsRef.current.length >= 3 ? "Press Enter to complete" : "Click to add points";
  const searchDisabled = !searchQuery.trim();

  const activeLotForCounts = () => {
    const selected = getSelectedLot();
    if (selected) return selected;
    return lotsRef.current[0] ?? null;
  };

  const metrics = (() => {
    const lot = activeLotForCounts();
    return {
      entrances: lot?.entrances.length ?? 0,
      exits: lot?.exits.length ?? 0,
      islands: lot?.islands.length ?? 0,
    };
  })();
  const baseAttribution =
    activeBase === "esri"
      ? "Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics"
      : "© OpenStreetMap contributors • © CARTO";
  const controlsEnabled = lotCount > 0;
  const shellClass = `mapper-shell${sidebarCollapsed ? " collapsed" : ""}${isMobile ? " mobile" : ""}${
    isMobile && mobileSidebarOpen ? " mobile-open" : ""
  }`;
  const sidebarHiddenMobile = isMobile && !mobileSidebarOpen;

  const layerToggleButtons = (
    <>
      <button
        type="button"
        className={`mapper-icon-btn${activeBase === "esri" ? " active" : ""}`}
        onClick={() => {
          const next = activeBaseRef.current === "esri" ? "carto" : "esri";
          setStatusMessage(
            `Switching to ${next === "esri" ? "satellite (Esri)" : "street (Carto)"} view`,
            "warn"
          );
          switchBaseRef.current(next);
        }}
        aria-label="Toggle base layer"
      >
        <img src="/assets/mapper/layers.svg" alt="" />
      </button>
      <button
        type="button"
        className={`mapper-icon-btn${shadeEnabled ? " active" : ""}`}
        onClick={() => {
          const map = getMap();
          const layer = shadowLayerRef.current;
          if (!map || !layer) return;
          if (shadeEnabled) {
            disableShadows();
          } else {
            if (map.getZoom() < MIN_SHADOW_ZOOM) {
              setStatusMessage("Zoom in to enable shadows", "warn", 1800);
              return;
            }
            // compute sunrise/sunset for this center and set slider bounds
            try {
              const times = computeSunriseSunset(shadeDate, map.getCenter().lat, map.getCenter().lng);
              // ensure sunrise < sunset; fall back to defaults if not
              let sunrise = times.sunrise;
              let sunset = times.sunset;
              if (!(sunrise < sunset)) {
                sunrise = 6;
                sunset = 18;
              }
              setShadeMinHour(sunrise);
              setShadeMaxHour(sunset);
              // set initial slider to the user's current local time (hours as fractional hour)
              const now = new Date();
              const nowHours = now.getHours() + now.getMinutes() / 60 + now.getSeconds() / 3600;
              const EPS_HOUR = 1 / 60; // one minute
              // clamp nowHours into [sunrise+EPS, sunset-EPS]
              const clamped = Math.min(Math.max(nowHours, sunrise + EPS_HOUR), sunset - EPS_HOUR);
              setShadeHour(clamped);
            } catch (e) {
              // ignore
            }
            shadeJustEnabledRef.current = true;
            layer.addTo(map);
            setShadeEnabled(true);
            shadowBuildingsRef.current = [];
            void refreshShadows();
          }
        }}
        aria-label="Toggle shade overlay"
      >
        <img src="/assets/mapper/shadow.svg" alt="Shadows" />
      </button>
      <button
        type="button"
        className="mapper-icon-btn"
        onClick={handleLocate}
        aria-label="Center on my location"
      >
        <img src="/assets/mapper/locate.svg" alt="" />
      </button>
    </>
  );

  const searchInput = (
    <div className="mapper-search-input">
      <span className="mapper-search-icon">
        <img src="/assets/SearchBarIcon.svg" alt="" />
      </span>
      <input
        type="text"
        placeholder="Search address or place"
        value={searchQuery}
        onChange={(e) => {
          setSearchQuery(e.target.value);
          setShowSuggestions(true);
        }}
        onFocus={() => {
          if (searchBlurTimerRef.current) {
            window.clearTimeout(searchBlurTimerRef.current);
            searchBlurTimerRef.current = null;
          }
          setSearchFocused(true);
          if (searchQuery.length >= 3) {
            void fetchSuggestions(searchQuery);
          }
          setShowSuggestions(true);
        }}
        onBlur={() => {
          // hide suggestions shortly after blur to allow click selection
          searchBlurTimerRef.current = window.setTimeout(() => {
            setShowSuggestions(false);
            setSearchFocused(false);
            searchBlurTimerRef.current = null;
          }, 140);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void handleSearch();
          }
        }}
      />
      {!isMobile && (
        <button
          type="button"
          className="mapper-search-btn"
          onClick={() => void handleSearch()}
          disabled={searchDisabled}
        >
          Search
        </button>
      )}
      {searchFocused && showSuggestions && (
        <div className="mapper-suggestion-list">
          {suggestions.length ? (
            suggestions.slice(0, isMobile ? 1 : 8).map((s, idx) => (
              <button
                key={`${s.name}-${idx}`}
                type="button"
                className="mapper-suggestion"
                onClick={() => handleSuggestionSelect(s)}
              >
                {formatSuggestionName(s.name)}
              </button>
            ))
          ) : (
            <div className="mapper-suggestion empty">No results</div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className={shellClass}>
      <div
        className={`mapper-sidebar${sidebarCollapsed ? " collapsed" : ""}${sidebarHiddenMobile ? " hidden" : ""}`}
        ref={sidebarRef}
      >
        <button
          type="button"
          className="mapper-collapse"
          onClick={() => {
            if (isMobile) {
              setMobileSidebarOpen((prev) => !prev);
            } else {
              setSidebarCollapsed((prev) => !prev);
            }
          }}
          aria-label={sidebarCollapsed ? "Expand panel" : "Collapse panel"}
        >
          <img
            src={sidebarCollapsed ? "/assets/nav/SidebarOpened.svg" : "/assets/nav/SidebarClosed.svg"}
            alt=""
          />
        </button>
        {showDeleteHint && (
          <div className="mapper-delete-hint">
            <img src="/assets/mapper/trash.svg" alt="Delete" />
            {!sidebarCollapsed}
          </div>
        )}
        <div className="mapper-brand-block">
          <div className="mapper-title">
            <img src="/assets/logos/Logo_Parallel.svg" alt="Parallel" className="mapper-logo-img" />
            {!sidebarCollapsed && <span className="mapper-title">Mapper</span>}
            {!sidebarCollapsed && <span className="mapper-beta">Beta</span>}
          </div>

        </div>

        <button
          type="button"
          className={`button-primary${isDetecting ? " is-loading" : ""}`}
          onClick={() => {
            void runActionThenClose(() => handleIdentifyLots());
          }}
          disabled={isDetecting}
          aria-label={isDetecting ? "Detecting lots" : lotCount > 0 ? "Re-detect lots" : "Auto-detect lots"}
          title={!zoomReady ? "Zoom in closer before auto-detecting lots" : undefined}
          data-disabled={!zoomReady}
        >
          <span className="button-primary-icon">
            <img
              src={
                isDetecting || lotCount > 0 ? "/assets/mapper/redetect.svg" : "/assets/mapper/auto-detect.svg"
              }
              alt=""
              className="button-primary-icon-img"
            />
          </span>
          {!sidebarCollapsed && (
            <span>{isDetecting ? "Detecting…" : lotCount > 0 ? "Re-Detect Lots" : "Auto-Detect Lots"}</span>
          )}
        </button>

        <button
          type="button"
          className={`mapper-add-lot-toggle${addLotMode ? " active" : ""}`}
          onClick={() => {
            void runActionThenClose(() => toggleAddLotMode());
          }}
          aria-label="Add lot"
          disabled={!zoomReady}
          data-disabled={!zoomReady}
        >
          <span className="mapper-add-lot-icon">
            <img src="/assets/superadmin/add-lot.svg" alt="" />
          </span>
          {!sidebarCollapsed && (
            <span className="mapper-add-lot-content">
              <span className="mapper-add-lot-label">Add Lot</span>
              <span className="mapper-add-lot-help">{addLotHelp}</span>
            </span>
          )}
        </button>

        <div className="mapper-control-list">
          {[
            {
              key: "both",
              label: "Entrance/Exit",
              count: metrics.entrances + metrics.exits,
              icon: "/assets/mapper/enter_exit.svg",
            },
            { key: "entrance", label: "Entrance", count: metrics.entrances, icon: "/assets/mapper/enter.svg" },
            { key: "exit", label: "Exit", count: metrics.exits, icon: "/assets/mapper/exit.svg" },
          ].map((item) => (
            <button
              key={item.key}
              type="button"
              className={`mapper-control${
                placementType === (item.key === "both" ? "both" : item.key) ? " active" : ""
              }${!controlsEnabled ? " disabled" : ""}`}
              disabled={!controlsEnabled}
              onPointerDown={(e) => {
                const type: AccessType = item.key === "both" ? "both" : (item.key as AccessType);
                handleControlPointerDown(type, e);
              }}
              onPointerUp={() => handleControlPointerUp()}
              onPointerLeave={() => handleControlPointerLeave()}
              onClick={(e) => {
                e.preventDefault();
                const type: AccessType = item.key === "both" ? "both" : (item.key as AccessType);
                void runActionThenClose(() => handleControlClick(type));
              }}
              title={sidebarCollapsed ? item.label : undefined}
            >
              <span className="mapper-control-bullet">
                <img src={item.icon} alt="" />
              </span>
              {!sidebarCollapsed && (
                <span className="mapper-control-text">
                  <span className="mapper-control-label">{item.label}</span>
                  <span className="mapper-control-meta">Currently {item.count}</span>
                </span>
              )}
              {!sidebarCollapsed && <span className="mapper-control-plus">+</span>}
            </button>
            ))}
        </div>

        <div className="mapper-export-row">
          <button
            type="button"
            className="button-secondary"
            onClick={() => {
              void runActionThenClose(() => downloadMapper());
            }}
            aria-label="Export .mapper"
          >
            <span className="button-secondary-icon">
              <img src="/assets/Download.svg" alt="" />
            </span>
            {!sidebarCollapsed && <span>Export</span>}
          </button>
          <button
            type="button"
            className="button-secondary"
            onClick={() => {
              void runActionThenClose(() => importInputRef.current?.click());
            }}
            aria-label="Import .mapper"
          >
            <span className="button-secondary-icon">
              <img src="/assets/Upload.svg" alt="" />
            </span>
            {!sidebarCollapsed && <span>Import</span>}
          </button>
        </div>

        {/* Delete lot intentionally removed per request */}
      </div>

      <div className={`mapper-map-shell${isMobile ? " mobile" : ""}`}>
        <input
          ref={importInputRef}
          type="file"
          accept=".mapper,application/json,text/plain"
          className="mapper-file-input"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              handleImportFile(file);
              e.target.value = "";
            }
          }}
        />
        <div className={`mapper-top-overlay${isMobile ? " mobile" : ""}`}>
          <div className={`mapper-search-bar${isMobile ? " mobile" : ""}`}>
            {searchInput}
            {!isMobile && <div className="mapper-layer-toggle">{layerToggleButtons}</div>}
          </div>
          {isMobile && (
            <div className="mapper-mobile-actions-row">
              {!mobileSidebarOpen && (
                <button
                  type="button"
                  className="mapper-mobile-menu"
                  onClick={() => setMobileSidebarOpen(true)}
                  aria-label="Open mapper menu"
                >
                  <img src="/assets/nav/SidebarOpened.svg" alt="" />
                </button>
              )}
              <div className="mapper-layer-toggle mobile">{layerToggleButtons}</div>
            </div>
          )}
        </div>
        <div className={`mapper-status-floating${statusVisible ? " show" : ""}`}>
          <span className={statusClass}>{status.text}</span>
        </div>
        <div
          className={`mapper-shade-time shade-map-overlay${shadeControlsVisible ? " show" : ""}`}
          aria-hidden={!shadeControlsVisible}
        >
          <div className="shade-date-wrap">
            <button
              type="button"
              className="shade-date"
              onClick={() => {
                const input = shadeDateInputRef.current;
                if (!input) return;
                if (input.showPicker) {
                  input.showPicker();
                  return;
                }
                input.focus({ preventScroll: true });
                input.click();
              }}
              disabled={!shadeControlsVisible}
            >
              {shadeDate.toLocaleDateString(undefined, { month: "short", day: "numeric" }).toUpperCase()}
            </button>
            <input
              ref={shadeDateInputRef}
              type="date"
              className="shade-date-input"
              value={formatDateInput(shadeDate)}
              onChange={(e) => {
                const val = e.target.value;
                if (!val) return;
                const next = parseDateInput(val);
                if (!next || Number.isNaN(next.getTime())) return;
                setShadeDate(next);
              }}
              disabled={!shadeControlsVisible}
            />
          </div>
          <img src="/assets/mapper/day.svg" alt="Daylight" className="shade-icon" />
          <input
            id="shade-hour"
            type="range"
            min={shadeMinHour}
            max={shadeMaxHour}
            step={0.25}
            value={shadeHour}
            onChange={(e) => {
              const next = parseFloat(e.target.value);
              setShadeHour(next);
            }}
            onInput={(e: React.FormEvent<HTMLInputElement>) => {
              const next = parseFloat((e.target as HTMLInputElement).value);
              setShadeHour(next);
            }}
            disabled={!shadeControlsVisible}
          />
          <img src="/assets/mapper/night.svg" alt="Nightfall" className="shade-icon" />
          <div className="shade-time-label">{formatShadeLabel(shadeHour)}</div>
          {/* flip toggle removed */}
          {/* sun readout removed per user preference */}
        </div>

        <div ref={mapContainerRef} className="mapper-map" aria-label="Parking lot mapper" />
        {!isMobile ? (
          <div className="mapper-info-card">
            <div className="mapper-info-line primary">{mapAddress}</div>
            <div className="mapper-info-line muted">{mapCoords || "—"}</div>
            <div className="mapper-info-line secondary">
              Leaflet {L.version} • Base: {activeBase === "esri" ? "Esri Imagery" : "Carto Streets"}
            </div>
            <div className="mapper-info-line secondary">{baseAttribution}</div>
          </div>
        ) : (
          <button
            type="button"
            className={`mapper-info-card${infoExpanded ? " expanded" : ""}`}
            onClick={() => setInfoExpanded((prev) => !prev)}
          >
            <div className="mapper-info-line primary">Leaflet</div>
            <div className="mapper-info-line secondary">
              Base: {activeBase === "esri" ? "Esri Imagery" : "Carto Streets"}
            </div>
            {infoExpanded && (
              <>
                <div className="mapper-info-line primary">{mapAddress}</div>
                <div className="mapper-info-line muted">{mapCoords || "—"}</div>
                <div className="mapper-info-line secondary">{baseAttribution}</div>
              </>
            )}
          </button>
        )}
        {paletteDrag && (
          <div
            className={`mapper-drag-ghost ${paletteDrag.type}`}
            style={{ left: `${paletteDrag.x}px`, top: `${paletteDrag.y}px` }}
          >
            <span className="mapper-drag-icon">
              <img
                src={
                  paletteDrag.type === "both"
                    ? "/assets/mapper/enter_exit.svg"
                    : paletteDrag.type === "exit"
                    ? "/assets/mapper/exit.svg"
                    : "/assets/mapper/enter.svg"
                }
                alt={paletteDrag.type}
              />
            </span>
          </div>
        )}
        {contextMenu && (
          <div
            className="mapper-context-anchor"
            style={{ left: `${contextMenu.x}px`, top: `${contextMenu.y}px` }}
          >
            <Menu
              trigger={<button type="button" className="mapper-context-trigger" aria-hidden />}
              items={contextMenu.items}
              placement="bottom-start"
              open
              onOpenChange={(openState) => {
                if (!openState) closeContextMenu();
              }}
              usePortal
              matchTriggerWidth={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Mapper;
